/**
 * Bronco Forge vehicle UI
 *   - "My Bronco" nav item (turned off, see boot at the bottom)
 *   - "What fits my Bronco?" popup (trim, doors, roof, bumper)
 *   - Collection bar above the product grid
 *   - Fit badge on product pages (+ "See X that fit" link when it won't fit)
 *   - Saved Bronco copied onto the cart, so it shows on the order
 *   - Shopping card + tray on blog posts (per-post overrides: custom.tray_*)
 *   - Fits / Won't fit labels on product lines inside posts ([product 1])
 *   - Facet conflict guard + homepage picker prefill
 *
 * Requires snippets/bf-fitment-core.liquid rendered in <head> first.
 * All user-controlled text is written with textContent, never innerHTML.
 */
(function () {
  'use strict';

  var BF = window.BFFitment;
  if (!BF) {
    if (window.console) {
      console.warn('[bf] bf-fitment-core snippet is missing from <head>; vehicle features are off.');
    }
    return;
  }

  var P = BF.PARAM_PREFIX;
  var BAR_ID = 'bf-vehicle-bar';
  var FACET_ORDER = ['trim', 'door', 'roof', 'bumper'];
  var FACET_TITLES = { trim: 'Trim', door: 'Doors', roof: 'Roof', bumper: 'Front bumper' };
  var CORE_FACETS = ['trim', 'door'];

  // Used only if the live filter list can't be loaded.
  var FALLBACK_FACETS = [
    {
      facet: 'trim',
      param: P + 'filter_trims',
      values: ['Base', 'Big Bend', 'Black Diamond', 'Outer Banks', 'Badlands', 'Wildtrak',
        'Everglades', 'Heritage Edition', 'Stroppe Edition', 'Raptor', 'First Edition'],
    },
    { facet: 'door', param: P + 'filter_door_config', values: ['2-Door', '4-Door'] },
    { facet: 'roof', param: P + 'roof_type_filter', values: ['Hard Top', 'Soft Top'] },
    { facet: 'bumper', param: P + 'front_bumper_filter', values: ['Plastic', 'Capable', 'Modular', 'Raptor Modular', 'Aftermarket'] },
  ];
  // JAQUAN CHANGE (v254): Sasquatch package. Asked in the popup (not a store filter, so not in the
  // collection bar). Fit checks read the product field custom.sasquatch_filter (empty = fits both).
  var SASQ_PARAM = P + 'sasquatch_filter';
  var SASQ_FACET = { facet: 'sasquatch', param: SASQ_PARAM, values: ['Sasquatch', 'No Sasquatch'] };
  // Ford made Sasquatch standard on these (pre-filled "Yes", still changeable). Raptor: not asked.
  var SASQ_STANDARD = ['wildtrak', 'everglades', 'heritage edition', 'heritage limited edition'];
  FALLBACK_FACETS.push(SASQ_FACET);

  var ICONS = {
    // JAQUAN CHANGE: fit labels everywhere use these. Green circle with a white
    // check, dark red circle with a white x, grey circle with a question mark.
    fitYes:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#111111"/><path d="m7 12.3 3.3 3.3L17.2 8.7" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    fitNo:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#9b1b14"/><path d="m8.3 8.3 7.4 7.4m0-7.4-7.4 7.4" stroke="#fff" stroke-width="2.6" stroke-linecap="round"/></svg>',
    // JAQUAN CHANGE: plain white check / x for the small slanted fit labels (miniFit).
    miniYes:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" fill="none" stroke="#fff" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    miniNo:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" fill="none" stroke="#fff" stroke-width="3.4" stroke-linecap="round"/></svg>',
    // Orange warning triangle for "Will this fit your Bronco?" (no Bronco saved yet).
    fitAsk:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.3 3.1a2 2 0 0 1 3.4 0l9 15.4A2 2 0 0 1 21 21.5H3a2 2 0 0 1-1.7-3l9-15.4z" fill="#ff6638"/><path d="M12 8.6v5.6" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/><circle cx="12" cy="17.6" r="1.4" fill="#fff"/></svg>',
    fitUnknown:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="#6b6b6b"/><path d="M9.6 9.4a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1.1.8-1.1 1.5v.4" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="17" r="1.2" fill="#fff"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    cross:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    question:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M9 9a3 3 0 1 1 4.2 2.7c-.8.4-1.2 1-1.2 1.8v.5"/><circle cx="12" cy="18" r=".6" fill="currentColor"/></svg>',
    close:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    // Orange "Verified" badge: filled circle with a white check.
    verified:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#ff6638"/><path d="m7 12.5 3.2 3.2L17 9" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>',
    // Small down arrow after the Bronco name in the header (opens the list of saved Broncos).
    chevron:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
  };

  /* ----------------------------- helpers ----------------------------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function icon(name, className) {
    var span = el('span', className || 'bf-icon');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = ICONS[name]; // static markup only
    return span;
  }

  // JAQUAN CHANGE: small slanted fit label, a mini version of the product page fit card:
  // green check (or red x) badge, then "Fits your Raptor 4-Door" in white on black.
  // Every label carries a hidden copy of the "Won't fit your ..." text, so all labels
  // are the same width and line up down a list.
  function miniFit(chip, fits, saved) {
    chip.classList.add('bf-mini');
    chip.classList.toggle('bf-mini--no', !fits);
    var badge = icon(fits ? 'miniYes' : 'miniNo', 'bf-mini__badge');
    var text = el('span', 'bf-mini__text');
    var shown = el('span', 'bf-mini__label');
    shown.append(fitLabel(fits ? 'Fits your ' : 'Won\u2019t fit your ', saved));
    var ghost = el('span', 'bf-mini__ghost');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.append(fitLabel('Won\u2019t fit your ', saved));
    text.append(shown, ghost);
    chip.replaceChildren(badge, text);
  }

  function button(label, className, onClick) {
    var b = el('button', className, label);
    b.type = 'button';
    if (onClick) b.addEventListener('click', onClick);
    return b;
  }

  function link(label, className, href) {
    var a = el('a', className, label);
    a.href = href;
    return a;
  }

  function summary(config) {
    return BF.vehicleParts(config).join(' · ');
  }

  function shortSummary(config) {
    return BF.vehicleParts(config).slice(0, 2).join(' · ');
  }

  // Plain-English vehicle names for the collection bar and badge:
  //   vehicleName   -> "Bronco Raptor 4-Door"
  //   vehicleExtras -> "With a Hard Top and a Modular bumper."
  function slotsOf(config) {
    var slots = { trim: [], door: [], roof: [], bumper: [] };
    Object.keys(config || {}).forEach(function (key) {
      var facet = BF.facetOfParam(key);
      if (!slots[facet]) return;
      [].concat(config[key]).forEach(function (v) {
        if (v && slots[facet].indexOf(v) === -1) slots[facet].push(v);
      });
    });
    return slots;
  }

  function vehicleName(config) {
    var s = slotsOf(config);
    return ['Bronco', s.trim.join(' or '), s.door.join(' or ')]
      .filter(Boolean)
      .join(' ');
  }

  // Same as vehicleName, but door values never split at the hyphen ("4-" / "DOOR").
  function vehicleNameNode(config) {
    var s = slotsOf(config);
    var frag = document.createDocumentFragment();
    frag.append(['Bronco', s.trim.join(' or ')].filter(Boolean).join(' '));
    s.door.forEach(function (d, i) {
      frag.append(i ? ' or ' : ' ', el('span', 'bf-nowrap', d));
    });
    return frag;
  }

  // JAQUAN CHANGE: the name used in every fit label: "Raptor 4-Door".
  function fitName(config) {
    var s = slotsOf(config);
    return [s.trim.join(' or '), s.door.join(' or ')].filter(Boolean).join(' ') || 'Bronco';
  }

  // Label text as nodes: "Fits your Raptor 4-Door", with the door kept on one
  // line so it never breaks as "4-" / "Door".
  function fitLabel(prefix, config) {
    var s = slotsOf(config);
    var label = el('span', 'bf-fitlabel');
    label.append(prefix + (s.trim.join(' or ') || (s.door.length ? '' : 'Bronco')));
    s.door.forEach(function (d, i) {
      label.append(i ? ' or ' : s.trim.length ? ' ' : '', el('span', 'bf-nowrap', d));
    });
    return label;
  }

  function withArticle(text) {
    return (/^(hd|[aeiou])/i.test(text) ? 'an ' : 'a ') + text;
  }

  function vehicleExtras(config) {
    var s = slotsOf(config);
    var items = [];
    if (s.roof.length) items.push(withArticle(s.roof.join(' or ')));
    if (s.bumper.length) {
      items.push(
        withArticle(
          s.bumper
            .map(function (b) {
              return /bumper/i.test(b) ? b : b + ' bumper';
            })
            .join(' or ')
        )
      );
    }
    return items.length ? 'With ' + items.join(' and ') + '.' : '';
  }

  // "Bronco Raptor 4-Door, Hard Top, Modular bumper" (for the order)
  function vehicleLine(config) {
    var s = slotsOf(config);
    var parts = [vehicleName(config)];
    if (s.roof.length) parts.push(s.roof.join(' or '));
    if (s.bumper.length) {
      parts.push(
        s.bumper
          .map(function (b) {
            return /bumper/i.test(b) ? b : b + ' bumper';
          })
          .join(' or ')
      );
    }
    return parts.join(', ');
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function allProductsUrl() {
    return (window.routes && window.routes.all_products_url) || rootUrl() + 'collections/all';
  }

  function isCollectionPage() {
    return !!document.querySelector('.collection-actions-filters-container');
  }

  function isProductPage() {
    return !!document.getElementById('bf-fitment-data');
  }

  function withoutVehicleParams(url) {
    var copy = new URL(url.toString());
    BF.stripVehicleParams(copy);
    copy.searchParams.delete('page');
    return copy.toString();
  }

  function withConfig(url, config) {
    var copy = new URL(url.toString(), window.location.origin);
    BF.applyConfigToUrl(copy, config);
    copy.searchParams.delete('page');
    copy.hash = ''; // JAQUAN CHANGE: never carry #my-bronco over, or the picker reopens after saving
    return copy.toString();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* --------------------------- My Bronco popup ------------------------ */
  // JAQUAN CHANGE (v248): one screen of four dropdowns (trim, doors, roof, bumper) that
  // saves by itself once all four are answered. No button. Close X sits in the
  // top-right corner of the screen. Optional image panel on desktop comes
  // from data-image on #bf-garage-config (in sections/header.liquid).

  var Garage = (function () {
    var root = null;
    var dialog = null;
    var form = null;
    var fieldsWrap = null;
    var primaryBtn = null;
    var removeBtn = null, verifiedBox, emailBox;
    var closeBtn = null;
    var facets = null;
    var fields = [];
    var draft = {};
    var lastFocus = null;
    var openTarget = null; // where "Show parts that fit" goes (default: all parts)
    var stayHere = false; // openHere(): save and close, don't leave the page
    var addMode = false; // openAdd(): "Add another Bronco", starts empty and adds to the list
    var titleEl = null;
    var subEl = null;
    var hideTimer = null;
    // JAQUAN CHANGE (v248): one screen with four dropdowns (Trim, Doors, Roof, Front bumper).
    // Once every dropdown has an answer it saves by itself: "Saved: ..." shows for a moment,
    // then the collection filters (or the popup closes on product pages). No save button.
    // Roof and bumper have "I'm not sure", which counts as an answer but isn't saved.
    var SKIP = '__bf_not_sure';
    var skipped = {}; // param -> true when they picked "I'm not sure"
    var sasqAuto = false; // Sasquatch "Yes" was filled in because the trim has it standard
    var dirty = false; // only save after they change something
    var saveTimer = null;
    var SAVE_DELAY = 900;
    var statusWrap = null, statusEl = null, dotsEl = null, savedBox = null, savedName = null, savedSub = null, savedKick = null;
    var photoNote = null, browseNote = null;
    var morePanel = null, moreOpen = false, moreIndex = 0; // JAQUAN CHANGE (v249): extra questions after saving
    var DD_LABELS = { trim: 'Trim', door: 'Doors', roof: 'Roof', bumper: 'Front bumper', sasquatch: 'Sasquatch package?' };
    var DD_PLACEHOLDER = { trim: 'Choose trim', door: 'Choose doors', roof: 'Choose roof', bumper: 'Choose bumper', sasquatch: 'Choose' };
    var DD_OPTION = { 'Sasquatch': 'Yes', 'No Sasquatch': 'No' };
    var OPTIONAL = { roof: true, bumper: true };
    var STEP_ICONS = {
      filter: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M230.6,49.53A15.81,15.81,0,0,0,216,40H40A16,16,0,0,0,28.19,66.76l.08.09L96,139.17V216a16,16,0,0,0,24.87,13.32l32-21.34A16,16,0,0,0,160,194.66V139.17l67.74-72.32.08-.09A15.8,15.8,0,0,0,230.6,49.53ZM146.18,130.58A8,8,0,0,0,144,136v58.66L112,216V136a8,8,0,0,0-2.16-5.47L40,56H216Z"/></svg>',
      close: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>',
      caret: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"/></svg>',
      tick: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>',
      saved: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"/></svg>',
      camera: '<svg viewBox="0 0 256 256" fill="currentColor"><path d="M208,56H180.28L166.65,35.56A8,8,0,0,0,160,32H96a8,8,0,0,0-6.65,3.56L75.71,56H48A24,24,0,0,0,24,80V192a24,24,0,0,0,24,24H208a24,24,0,0,0,24-24V80A24,24,0,0,0,208,56Zm8,136a8,8,0,0,1-8,8H48a8,8,0,0,1-8-8V80a8,8,0,0,1,8-8H80a8,8,0,0,0,6.66-3.56L100.28,48h55.43l13.63,20.44A8,8,0,0,0,176,72h32a8,8,0,0,1,8,8ZM128,88a44,44,0,1,0,44,44A44.05,44.05,0,0,0,128,88Zm0,72a28,28,0,1,1,28-28A28,28,0,0,1,128,160Z"/></svg>',
    };
    function stepIcon(name, extra) {
      var span = el('span', 'bf-garage__ic' + (extra ? ' ' + extra : ''));
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = STEP_ICONS[name]; // static markup only
      return span;
    }

    function parseFacets(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var found = [];
      doc.querySelectorAll('select[data-param-name]').forEach(function (select) {
        var param = select.dataset.paramName;
        if (!BF.isVehicleParam(param)) return;
        var facet = BF.facetOfParam(param);
        var values = [];
        select.querySelectorAll('option').forEach(function (opt) {
          var v = (opt.value || '').trim();
          if (v && values.indexOf(v) === -1) values.push(v);
        });
        if (!values.length) return;

        var fallback = FALLBACK_FACETS.filter(function (f) {
          return f.facet === facet;
        })[0];
        if (fallback) {
          values.sort(function (a, b) {
            var ia = fallback.values.indexOf(a);
            var ib = fallback.values.indexOf(b);
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
          });
        }
        found.push({ facet: facet, param: param, values: values });
      });
      found.sort(function (a, b) {
        return FACET_ORDER.indexOf(a.facet) - FACET_ORDER.indexOf(b.facet);
      });
      if (found.length) found.push(SASQ_FACET);
      return found.length ? found : FALLBACK_FACETS;
    }

    var FACETS_CACHE_KEY = 'bf_garage_facets_v4'; // v4: + Sasquatch
    var FACETS_MAX_AGE = 24 * 60 * 60 * 1000;
    var facetsLoading = null;
    var pendingFacets = null;

    // JAQUAN CHANGE: the choices are kept on this device for a day, so the popup opens with
    // them right away; an older copy is still used and refreshed in the background.
    function readFacetCache() {
      try {
        var c = JSON.parse(localStorage.getItem(FACETS_CACHE_KEY) || 'null');
        if (c && Array.isArray(c.list) && c.list.length) return c;
      } catch (e) {}
      return null;
    }

    function loadFacets() {
      if (facets) return Promise.resolve(facets);
      var cached = readFacetCache();
      if (cached) {
        facets = cached.list;
        if (Date.now() - (cached.at || 0) > FACETS_MAX_AGE) fetchFacets();
        return Promise.resolve(facets);
      }
      return fetchFacets();
    }

    function fetchFacets() {
      if (facetsLoading) return facetsLoading;
      var url = new URL(allProductsUrl(), window.location.origin);
      url.searchParams.set('view', 'do_not_use');
      facetsLoading = fetch(url.toString())
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(parseFacets)
        .catch(function () {
          return FALLBACK_FACETS;
        })
        .then(function (list) {
          facetsLoading = null;
          var changed = !facets || JSON.stringify(facets) !== JSON.stringify(list);
          if (list !== FALLBACK_FACETS) {
            try {
              localStorage.setItem(FACETS_CACHE_KEY, JSON.stringify({ at: Date.now(), list: list }));
            } catch (e) {}
          }
          if (!facets) {
            facets = list;
          } else if (changed && list !== FALLBACK_FACETS) {
            // A fresher list: use it now, or on the next open if someone is mid-pick.
            if (root && !root.hidden && root.contains(document.activeElement)) {
              pendingFacets = list;
            } else {
              facets = list;
              if (fields.length) {
                buildFields();
                if (root && !root.hidden) {
                  prune();
                  sync();
                }
              }
            }
          }
          return facets;
        });
      return facetsLoading;
    }

    function selectionsBefore(index) {
      var out = [];
      for (var i = 0; i < index; i++) {
        var p = facets[i].param;
        if (draft[p]) out.push({ name: p, value: draft[p] });
      }
      return out;
    }

    function trimPicked() {
      return String(draft[P + 'filter_trims'] || '').toLowerCase();
    }

    function allowedValues(index) {
      var f = facets[index];
      if (f.facet === 'sasquatch') return trimPicked() === 'raptor' ? [] : f.values.slice();
      var before = selectionsBefore(index);
      return f.values.filter(function (v) {
        return !BF.isOptionBlocked(f.param, v, before);
      });
    }

    // Drop any pick that no longer fits the picks above it (Doors after a new trim).
    // Only one choice left (Raptor, Everglades: 4-Door): pick it.
    function prune() {
      facets.forEach(function (f, i) {
        var v = draft[f.param];
        if (v && allowedValues(i).indexOf(v) === -1) delete draft[f.param];
      });
      if (!facets[0] || !draft[facets[0].param]) return;
      facets.forEach(function (f, i) {
        if (i === 0 || draft[f.param] || OPTIONAL[f.facet]) return;
        var vals = allowedValues(i);
        if (vals.length === 1) draft[f.param] = vals[0];
      });
      var standard = SASQ_STANDARD.indexOf(trimPicked()) !== -1;
      if (sasqAuto && !standard) {
        // "Yes" was filled in for the old trim; this trim needs an answer.
        delete draft[SASQ_PARAM];
        sasqAuto = false;
      }
      if (!draft[SASQ_PARAM] && standard) {
        draft[SASQ_PARAM] = 'Sasquatch';
        sasqAuto = true;
      }
    }

    function buildFields() {
      fields = facets.map(function (f) {
        return { facet: f };
      });
      if (!fieldsWrap) return;
      fieldsWrap.replaceChildren();
      fields.forEach(function (fd, i) {
        var f = fd.facet;
        var wrap = el('div', 'bf-dd');
        var id = 'bf-dd-' + f.facet;
        var label = el('label', 'bf-dd__label', DD_LABELS[f.facet] || FACET_TITLES[f.facet] || f.facet);
        label.setAttribute('for', id);
        var box = el('div', 'bf-dd__box');
        var sel = document.createElement('select');
        sel.id = id;
        sel.className = 'bf-dd__select';
        sel.addEventListener('change', function () {
          choose(i, sel.value);
        });
        box.append(sel, stepIcon('caret', 'bf-dd__ic bf-dd__ic--caret'), stepIcon('tick', 'bf-dd__ic bf-dd__ic--tick'));
        wrap.append(label, box);
        // JAQUAN CHANGE (v254): Sasquatch is a Yes / No question: two buttons instead of a dropdown.
        if (f.facet === 'sasquatch') {
          wrap.classList.add('bf-dd--yn');
          sel.tabIndex = -1;
          sel.setAttribute('aria-hidden', 'true');
          label.removeAttribute('for');
          label.id = id + '-label';
          var yn = el('div', 'bf-dd__yn');
          yn.setAttribute('role', 'group');
          yn.setAttribute('aria-labelledby', label.id);
          fd.yn = ['Sasquatch', 'No Sasquatch'].map(function (v) {
            var b = button(DD_OPTION[v], 'bf-dd__ynb', function () {
              choose(i, v);
            });
            b.dataset.value = v;
            b.setAttribute('aria-pressed', 'false');
            yn.append(b);
            return b;
          });
          wrap.append(yn);
        }
        fd.wrap = wrap;
        fd.select = sel;
        fieldsWrap.append(wrap);
      });
    }

    function fieldsReady() {
      return !!(facets && fields.length === facets.length && fields[0] && fields[0].select);
    }

    function answered(i) {
      var p = facets[i].param;
      return !!draft[p] || (OPTIONAL[facets[i].facet] && !!skipped[p]) || allowedValues(i).length === 0;
    }

    // Trim picked and every other dropdown answered.
    function isComplete() {
      if (!facets || !facets[0] || !draft[facets[0].param]) return false;
      return facets.every(function (f, i) {
        return answered(i);
      });
    }

    function choose(i, value) {
      var p = facets[i].param;
      if (p === SASQ_PARAM) sasqAuto = false; // their own answer
      delete skipped[p];
      delete draft[p];
      if (value === SKIP) skipped[p] = true;
      else if (value) draft[p] = value;
      dirty = true;
      prune();
      sync();
    }

    // A saved Bronco without a roof or bumper was saved with "I'm not sure".
    function skippedFromSaved() {
      skipped = {};
      if (!BF.getSaved()) return;
      facets.forEach(function (f) {
        if (OPTIONAL[f.facet] && !draft[f.param]) skipped[f.param] = true;
      });
    }

    function leavesPage() {
      return !(stayHere || (isProductPage() && !isCollectionPage()));
    }

    function sync() {
      if (!facets || !fieldsWrap) return;
      if (!fieldsReady()) buildFields();
      var trimOn = !!draft[facets[0].param];
      var done = 0;
      fields.forEach(function (fd, i) {
        var f = fd.facet;
        var p = f.param;
        var sel = fd.select;
        var opts = [new Option(DD_PLACEHOLDER[f.facet] || 'Choose', '')];
        var allowedNow = allowedValues(i);
        allowedNow.forEach(function (v) {
          opts.push(new Option(f.facet === 'sasquatch' ? DD_OPTION[v] || v : v, v));
        });
        fd.wrap.hidden = f.facet === 'sasquatch' && allowedNow.length === 0;
        if (fd.yn) {
          fd.yn.forEach(function (b) {
            var on = (draft[f.param] || '') === b.dataset.value;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        }
        if (OPTIONAL[f.facet]) opts.push(new Option('I’m not sure', SKIP));
        sel.replaceChildren.apply(sel, opts);
        sel.value = draft[p] || (skipped[p] ? SKIP : '');
        // Doors waits for Trim: its choices depend on the trim.
        sel.disabled = f.facet === 'door' && !trimOn;
        var on = !!sel.value;
        fd.wrap.classList.toggle('is-done', on);
        fd.wrap.classList.toggle('is-disabled', sel.disabled);
        sel.classList.toggle('is-empty', !on);
        if (answered(i)) done++;
      });
      // JAQUAN CHANGE (v250): the next box to fill in is the white one.
      var nextSet = false;
      fields.forEach(function (fd, i) {
        var isNext = !nextSet && !fd.select.disabled && !answered(i);
        if (isNext) nextSet = true;
        fd.wrap.classList.toggle('is-next', isNext);
      });

      dotsEl.replaceChildren.apply(
        dotsEl,
        facets.map(function (f, i) {
          return el('i', answered(i) ? 'is-on' : null);
        })
      );

      var complete = isComplete();
      var saved = BF.getSaved();
      clearTimeout(saveTimer);
      saveTimer = null;
      if (complete && dirty) {
        savedKick.textContent = 'Saved';
        savedName.textContent = summary(draft);
        savedSub.textContent = leavesPage() ? 'Showing parts that fit your Bronco.' : 'Fit checks on this page now use it.';
        savedBox.hidden = false;
        statusWrap.hidden = true;
        saveTimer = setTimeout(function () {
          saveTimer = null;
          if (!saveNow()) return;
          finish(); // JAQUAN CHANGE (v254): no extra questions after saving
        }, SAVE_DELAY);
      } else if (complete && saved && !addMode) {
        savedKick.textContent = 'Your Bronco';
        savedName.textContent = summary(draft);
        savedSub.textContent = 'Change any box and we’ll save it again.';
        savedBox.hidden = false;
        statusWrap.hidden = true;
      } else {
        savedBox.hidden = true;
        statusWrap.hidden = false;
        statusEl.textContent = done + ' of ' + facets.length + ' picked';
      }
      root.classList.toggle('is-complete', complete);
      removeBtn.hidden = addMode || !saved;
    }

    function saveNow() {
      if (addMode) Broncos.markAdd();
      if (!BF.saveConfig(draft)) return false;
      addMode = false;
      dirty = false;
      return true;
    }

    // After saving: filter the collection (or go to the parts list), or just close.
    function finish() {
      var saved = BF.getSaved();
      if (!saved) return;
      if (!leavesPage()) {
        close();
        return;
      }

      var target = isCollectionPage() ? window.location.href : openTarget || allProductsUrl();
      var url = new URL(withConfig(target, saved));
      BF.markApplied(url.pathname);
      var here = new URL(window.location.href);
      here.hash = '';
      if (url.toString() === here.toString()) {
        // Already showing this Bronco's parts: just close.
        if (window.location.hash) history.replaceState(null, '', here.toString());
        close();
        return;
      }
      flagFitPlay();
      window.location.href = url.toString();
    }

    // Enter key in a dropdown.
    function apply(event) {
      if (event) event.preventDefault();
      if (!isComplete()) return;
      clearTimeout(saveTimer);
      saveTimer = null;
      if (saveNow()) finish();
    }

    function removeVehicle() {
      clearTimeout(saveTimer);
      saveTimer = null;
      draft = {};
      sasqAuto = false;
      skipped = {};
      dirty = false;
      // Removes this Bronco; another saved Bronco (if any) takes its place.
      if (Broncos.removeActive()) return;
      draft = Object.assign({}, BF.getSaved() || {});
      sasqAuto = false;
      skippedFromSaved();
      sync();
    }

    function trapFocus(event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = root.querySelectorAll('button:not([disabled]):not([hidden]), a[href], select:not([disabled])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function imageUrl() {
      var host = document.getElementById('bf-garage-config') || document.getElementById('bf-navslot');
      var src = host && host.dataset.image;
      return src && /^(https?:)?\/\//.test(src) ? src : '';
    }

    // JAQUAN CHANGE (v249): "Want better matches?" Year, Tires, Lift, one tap each. Every tap saves.
    // "Skip this one", "Maybe later" (not again this visit) or the X: the page filters as usual.
    function showMore() {
      moreOpen = true;
      moreIndex = 0;
      root.classList.add('is-more');
      morePanel.hidden = false;
      renderMore();
    }

    function endMore() {
      moreOpen = false;
      root.classList.remove('is-more');
      morePanel.hidden = true;
      finish();
    }

    function renderMore() {
      var left = moreLeft(BF.getSaved());
      var all = moreQuestions(BF.getSaved());
      if (moreIndex >= left.length) {
        endMore();
        return;
      }
      var q = left[moreIndex];
      var n = all.length - left.length + moreIndex + 1;
      var head = el('div', 'bf-more__h');
      head.append(el('p', 'bf-more__t', 'Want better matches?'), el('span', 'bf-more__n', n + ' of ' + all.length));
      var bar = el('div', 'bf-more__bar');
      bar.setAttribute('aria-hidden', 'true');
      for (var i = 0; i < all.length; i++) bar.append(el('i', i < n ? 'is-on' : null));
      var links = el('div', 'bf-more__links');
      links.append(
        button('Skip this one', 'bf-textlink bf-more__skip', function () {
          moreIndex++;
          renderMore();
        }),
        button('Maybe later', 'bf-textlink', function () {
          try {
            sessionStorage.setItem(MORE_LATER, '1');
          } catch (e) {}
          endMore();
        })
      );
      morePanel.replaceChildren(
        head,
        bar,
        moreQuestionEl(q, function () {
          renderMore();
        }),
        links
      );
      var first = morePanel.querySelector('.bf-mq__chip');
      if (first) first.focus();
    }

    var TITLE = 'Select your Bronco'; // JAQUAN CHANGE (v250): same words as the collection bar

    function build() {
      if (root) return;

      root = el('div', 'bf-garage bf-garage--steps bf-garage--dd');
      root.hidden = true;

      var backdrop = el('div', 'bf-garage__backdrop');
      backdrop.addEventListener('click', close);

      dialog = el('div', 'bf-garage__dialog');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'bf-garage-title');

      var content = el('div', 'bf-garage__content');

      var top = el('div', 'bf-dd-top');
      var head = el('div', 'bf-dd-head');
      var title = el('h2', 'bf-garage__title', TITLE);
      title.id = 'bf-garage-title';
      titleEl = title;
      var sub = el(
        'p',
        'bf-dd-sub',
        'Answer these and we only show parts that fit'
      );
      subEl = sub;
      head.append(title, sub);
      closeBtn = button('', 'bf-garage__round bf-garage__close', close);
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.append(stepIcon('close'));
      // JAQUAN CHANGE (v250): orange slanted tag with the filter icon, like the collection bar.
      var tag = el('span', 'bf-dd-tag');
      tag.append(stepIcon('filter'));
      top.append(tag, head, closeBtn);

      form = el('form', 'bf-garage__form');
      form.noValidate = true;
      form.addEventListener('submit', apply);
      fieldsWrap = el('div', 'bf-dd-grid');
      fieldsWrap.append(el('p', 'bf-garage__loading', 'Loading…'));
      form.append(fieldsWrap);

      // They can text a photo and we tell them which bumper they have.
      photoNote = el('p', 'bf-dd-note');
      var photoText = el('span', null, 'Not sure which bumper? ');
      photoText.append(link('Text us a photo', 'bf-textlink', 'sms:+19097728050?&body=' + encodeURIComponent('Which front bumper do I have? Photo attached.')));
      photoNote.append(stepIcon('camera'), photoText);

      statusWrap = el('div', 'bf-dd-status');
      dotsEl = el('span', 'bf-dd-dots');
      dotsEl.setAttribute('aria-hidden', 'true');
      statusEl = el('span', 'bf-dd-count');
      statusWrap.append(dotsEl, statusEl);

      savedBox = el('div', 'bf-dd-saved');
      savedBox.hidden = true;
      // JAQUAN CHANGE (v249): a solid black box: "Your Bronco" / "Saved" small, the Bronco big.
      savedKick = el('span', 'bf-dd-saved__k');
      savedName = el('b');
      savedSub = el('small');
      var savedText = el('div');
      savedText.append(savedKick, savedName, savedSub);
      savedBox.append(stepIcon('saved'), savedText);

      // Screen readers hear the count and "Saved: ..." as they change.
      var live = el('div', 'bf-dd-live');
      live.setAttribute('aria-live', 'polite');
      live.append(statusWrap, savedBox);

      var foot = el('div', 'bf-dd-foot');
      browseNote = el('p', 'bf-garage__note');
      browseNote.append('Just browsing? ', link('Shop all parts', 'bf-textlink', allProductsUrl()));
      removeBtn = button('Remove my Bronco', 'bf-textlink bf-garage__remove', removeVehicle);
      removeBtn.hidden = true;
      foot.append(browseNote, removeBtn);

      morePanel = el('div', 'bf-more');
      morePanel.hidden = true;
      content.append(top, form, photoNote, live, morePanel, foot);
      dialog.append(content);

      root.append(backdrop, dialog);
      root.addEventListener('keydown', trapFocus);
      document.body.append(root);
    }

    function open() {
      openTarget = null;
      stayHere = false;
      addMode = false;
      build();
      clearTimeout(hideTimer); // JAQUAN CHANGE: a close from just before can't hide it again
      clearTimeout(saveTimer);
      saveTimer = null;
      lastFocus = document.activeElement;
      draft = Object.assign({}, BF.getSaved() || {});
      sasqAuto = false;
      skipped = {};
      dirty = false;
      moreOpen = false;
      root.classList.remove('is-more');
      morePanel.hidden = true;
      titleEl.textContent = TITLE;
      root.hidden = false;
      document.documentElement.classList.add('bf-garage-open');
      root.classList.add('is-open');
      removeBtn.hidden = !BF.getSaved();
      closeBtn.focus();

      if (pendingFacets) {
        facets = pendingFacets;
        pendingFacets = null;
        fields = [];
      }
      // First visit, store list not here yet: open with the built-in list now and swap
      // the store's list in when it arrives (see fetchFacets).
      if (!facets && !readFacetCache()) {
        fetchFacets();
        facets = FALLBACK_FACETS;
      }
      loadFacets().then(function () {
        if (!fieldsReady()) buildFields();
        if (!addMode) skippedFromSaved();
        prune();
        sync();
      });
    }

    function close() {
      if (!root || root.hidden) return;
      // Closed during the "Saved" moment: still save, just don't leave the page.
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
        saveNow();
      }
      root.classList.remove('is-open');
      document.documentElement.classList.remove('bf-garage-open');
      clearTimeout(hideTimer);
      root.hidden = true;
      if (lastFocus && lastFocus.focus) lastFocus.focus();
      // Closed while the extra questions were up: the Bronco is saved, so filter the page now.
      if (moreOpen) {
        moreOpen = false;
        root.classList.remove('is-more');
        morePanel.hidden = true;
        finish();
      }
    }

    // Same popup, but after saving it lands on this collection.
    function openFor(url) {
      open();
      openTarget = url;
    }

    // Same popup, but saving keeps you on this page (blog tray with hand-picked parts).
    function openHere() {
      open();
      stayHere = true;
      if (fieldsReady()) sync();
    }

    // JAQUAN CHANGE: "+ Add another Bronco": same popup, empty, and saving adds it to the
    // saved Broncos (it becomes the one in use) instead of replacing the current one.
    function openAdd() {
      open();
      addMode = true;
      draft = {};
      sasqAuto = false;
      skipped = {};
      titleEl.textContent = 'Add another Bronco';
      removeBtn.hidden = true;
      if (fieldsReady()) sync();
    }

    return { open: open, openFor: openFor, openHere: openHere, openAdd: openAdd, close: close, loadFacets: loadFacets };
  })();

  window.BFGarage = Garage;

  /* --------------- popup "Email me parts that fit" box --------------- */
  // JAQUAN CHANGE. Signs the shopper up to the Klaviyo list "Parts that fit my Bronco"
  // (W79urq) as subscribed to email marketing (signing up here is the consent), puts
  // their Bronco on the Klaviyo profile (Bronco Trim, Bronco Doors, Bronco Roof,
  // Bronco Bumper, Bronco Setup) and saves it as their Bronco on the site.

  var PARTS_LIST_ID = 'W79urq';
  var PARTS_DONE_KEY = 'bf_parts_email';
  var KLAVIYO_COMPANY = 'UbzPDG';

  function klaviyoPost(path, body) {
    return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + path + ' ' + res.status);
    });
  }

  function partsSignup(email, config) {
    var s = slotsOf(config);
    var sub = klaviyoPost('subscriptions', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Bronco popup',
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: { list: { data: { type: 'list', id: PARTS_LIST_ID } } }
      }
    });
    // A failure here doesn't block the signup.
    var props = klaviyoPost('profiles', {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: {
            'Bronco Trim': s.trim.join(' or '),
            'Bronco Doors': s.door.join(' or '),
            'Bronco Roof': s.roof.join(' or '),
            'Bronco Bumper': s.bumper.join(' or '),
            'Bronco Setup': vehicleName(config)
          }
        }
      }
    }).catch(function () {});
    return Promise.all([sub, props]);
  }

  // JAQUAN CHANGE: "What fits my Bronco?" popup: the picked Bronco as the slanted fit card.
  function garageCard(config) {
    var card = el('div', 'bf-fit bf-fitcard bf-garage__card');
    var badge = el('div', 'bf-fitcard__badge');
    badge.append(icon('miniYes', 'bf-garage__card-check'));
    var text = el('div', 'bf-fitcard__text');
    text.append(el('p', 'bf-fitcard__v', 'Ford ' + vehicleName(config)));
    var extras = vehicleExtras(config);
    if (extras) text.append(el('p', 'bf-fitcard__d', extras));
    card.append(badge, text);
    return card;
  }

  function partsEmailBox(getConfig) {
    var box = el('div', 'bf-garage__email');
    var done = el('p', 'bf-garage__email-done');
    var doneSet = function () {
      done.replaceChildren(el('b', '', 'You’re signed up.'), ' We’ll email you parts that fit your Bronco.');
    };

    var title = el('p', 'bf-garage__email-title', 'Email me parts that fit my Bronco');
    var text = el('p', 'bf-garage__email-text', 'New parts and deals for your setup. Unsubscribe anytime.');
    var form = el('form', 'bf-garage__email-form');
    form.noValidate = true;
    var label = el('label', 'bf-sr', 'Email');
    label.htmlFor = 'bf-garage-email';
    var input = el('input', 'bf-garage__email-input');
    input.type = 'email';
    input.id = 'bf-garage-email';
    input.name = 'email';
    input.autocomplete = 'email';
    input.placeholder = 'Email address';
    var send = el('button', 'bf-garage__email-btn', 'Email me');
    send.type = 'submit';
    var msg = el('p', 'bf-garage__email-msg');
    msg.setAttribute('role', 'status');
    msg.hidden = true;
    form.append(label, input, send);
    box.append(title, text, form, msg, done);

    var already = false;
    try {
      already = !!window.localStorage.getItem(PARTS_DONE_KEY);
    } catch (e) {}
    if (already) {
      doneSet();
      title.hidden = text.hidden = form.hidden = true;
    } else {
      done.hidden = true;
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var email = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        msg.textContent = 'Enter a valid email address.';
        msg.hidden = false;
        input.focus();
        return;
      }
      var config = getConfig();
      msg.hidden = true;
      send.disabled = true;
      send.textContent = 'Signing up…';
      partsSignup(email, config)
        .then(function () {
          try {
            window.localStorage.setItem(PARTS_DONE_KEY, '1');
          } catch (e) {}
          BF.saveConfig(config);
          doneSet();
          title.hidden = text.hidden = form.hidden = true;
          done.hidden = false;
        })
        .catch(function () {
          msg.textContent = 'Something went wrong. Please try again.';
          msg.hidden = false;
          send.disabled = false;
          send.textContent = 'Email me';
        });
    });
    return box;
  }

  /* ------------------------- more than one Bronco ------------------------- */
  // JAQUAN CHANGE: up to 3 saved Broncos. The one in use stays where it always was
  // (BF.getSaved(), used by fit checks, collections and the order); the full list is
  // kept beside it in localStorage. Picking one from the list makes it the one in use.
  // Saving from the popup edits the one in use; "+ Add another Bronco" adds one.
  // Removing the one in use switches to the next saved Bronco.

  var Broncos = (function () {
    var LIST_KEY = 'bf_vehicle_list';
    var MAX = 3;
    var LIST_EVENT = 'bf:vehicle-list-change';
    var pendingAdd = false;
    var lastActive = null;

    function same(a, b) {
      return !!(a && b && BF.sameConfig(a, b));
    }

    function indexIn(list, config) {
      for (var i = 0; i < list.length; i++) if (same(list[i], config)) return i;
      return -1;
    }

    function read() {
      var raw = [];
      try {
        raw = JSON.parse(window.localStorage.getItem(LIST_KEY) || '[]');
      } catch (e) {
        raw = [];
      }
      var out = [];
      (Array.isArray(raw) ? raw : []).forEach(function (c) {
        var clean = BF.sanitizeConfig(c);
        if (clean && indexIn(out, clean) === -1) out.push(clean);
      });
      return out.slice(0, MAX);
    }

    function write(list) {
      try {
        window.localStorage.setItem(LIST_KEY, JSON.stringify(list));
      } catch (e) {}
      try {
        window.dispatchEvent(new CustomEvent(LIST_EVENT));
      } catch (e) {}
    }

    function trim(list, keep) {
      while (list.length > MAX) {
        var k = 0;
        while (k < list.length - 1 && same(list[k], keep)) k++;
        list.splice(k, 1);
      }
      return list;
    }

    // The saved list, with the Bronco in use always in it.
    function all() {
      var list = read();
      var now = BF.getSaved();
      if (now && indexIn(list, now) === -1) {
        list.push(now);
        write(trim(list, now));
      }
      return list;
    }

    function onChange() {
      var now = BF.getSaved();
      var prev = lastActive;
      var list = read();
      if (now) {
        if (indexIn(list, now) === -1) {
          var p = prev && !pendingAdd ? indexIn(list, prev) : -1;
          if (p > -1) list[p] = now; // edited the one in use
          else list.push(now); // added one (or the first one)
        }
        pendingAdd = false;
        lastActive = now;
        write(trim(list, now));
        return;
      }
      // The one in use was removed: drop it and switch to the next saved Bronco.
      pendingAdd = false;
      if (prev) {
        var i = indexIn(list, prev);
        if (i > -1) list.splice(i, 1);
      }
      lastActive = null;
      write(list);
      if (list.length) BF.saveConfig(list[0]);
    }

    function markAdd() {
      pendingAdd = true;
    }

    // Collections filtered to a Bronco follow the one in use.
    function followOnCollection() {
      if (!isCollectionPage() || !BF.vehicleParamsFromSearch(window.location.search)) return false;
      var now = BF.getSaved();
      var base = withoutVehicleParams(window.location.href);
      if (now) {
        var url = new URL(withConfig(base, now));
        BF.markApplied(url.pathname);
        flagFitPlay();
        window.location.href = url.toString();
      } else {
        window.location.href = base;
      }
      return true;
    }

    function use(config) {
      if (same(config, BF.getSaved())) return;
      BF.saveConfig(config);
      followOnCollection();
    }

    // Returns true when the page is about to reload.
    function removeActive() {
      BF.clearSaved();
      return followOnCollection();
    }

    function remove(config) {
      if (same(config, BF.getSaved())) return removeActive();
      var list = read();
      var i = indexIn(list, config);
      if (i > -1) {
        list.splice(i, 1);
        write(list);
      }
      return false;
    }

    lastActive = BF.getSaved();
    all();
    window.addEventListener(BF.CHANGE_EVENT, onChange);
    // Another tab changed things: it already updated the list, just catch up.
    window.addEventListener('storage', function () {
      lastActive = BF.getSaved();
    });

    return { all: all, use: use, remove: remove, removeActive: removeActive, markAdd: markAdd, MAX: MAX, LIST_EVENT: LIST_EVENT };
  })();

  /* ---------------- header / phone menu "My Bronco" box ---------------- */
  // JAQUAN CHANGE. Saved Bronco:
  //   ┌ (✓) Verified ───────────────┐
  //   │ Ford Bronco Raptor 4-Door  ✕ │   name opens the popup, ✕ removes the Bronco
  //   └─────────────────────────────┘
  // Nothing saved: an "Add your Bronco" box that opens the popup.
  // The popup shows the same box (no ✕) once every step is picked.

  function chipBox(config, opts) {
    opts = opts || {};
    var box = el('fieldset', 'bf-chip is-saved' + (opts.popup ? ' bf-chip--popup' : ''));
    var legend = el('legend', 'bf-chip__badge');
    legend.append(icon('verified', 'bf-chip__check'), 'Verified');
    box.append(legend);
    var name = 'Ford ' + vehicleName(config);
    if (opts.popup) {
      var copy = el('div', 'bf-chip__copy');
      copy.append(el('span', 'bf-chip__name', name));
      var extras = vehicleExtras(config);
      if (extras) copy.append(el('span', 'bf-chip__extras', extras));
      box.append(copy);
      return box;
    }
    // JAQUAN CHANGE: the name opens the list of saved Broncos (header dropdown).
    var change = button(name, 'bf-chip__name', opts.onName || Garage.open);
    change.setAttribute('aria-label', 'My Bronco: ' + vehicleLine(config) + '. Show my Broncos');
    if (opts.onName) {
      change.setAttribute('aria-expanded', 'false');
      change.append(icon('chevron', 'bf-chip__caret'));
    }
    var clear = button('', 'bf-chip__clear', clearChip);
    clear.setAttribute('aria-label', 'Remove my Bronco');
    clear.append(icon('close'));
    box.append(change, clear);
    return box;
  }

  function clearChip() {
    Broncos.removeActive();
  }

  // JAQUAN CHANGE: the saved Broncos as a list. Tap one to use it, ✕ removes it.
  //   (•) Badlands 2-Door            ✕
  //       Hard top · Plastic bumper
  //   ( ) Raptor 4-Door              ✕
  //   + Add another Bronco      Edit
  // JAQUAN CHANGE: phone menu (inMenu): only the active Bronco shows on the menu's main
  // screen (CSS); with more than one saved, "Switch Bronco" opens the full list as its own
  // screen in the drawer (bf-mmenu--broncos, Back in header-mobile-menu.liquid). Picking
  // one goes back to the main screen.
  function menuBroncos(slot, on) {
    var menu = slot && slot.closest('.bf-mmenu');
    if (!menu) return;
    menu.classList.toggle('bf-mmenu--broncos', on);
    var inner = menu.querySelector('.mobile-menu-inner');
    if (inner) inner.scrollTop = 0;
    var back = menu.querySelector('[data-bf-mnav-back]');
    if (on && back) back.focus({ preventScroll: true });
  }

  function broncoList(onDone, menuSlot) {
    var saved = BF.getSaved();
    var wrap = el('div', 'bf-broncos__body');
    var list = el('ul', 'bf-broncos__list');
    Broncos.all().forEach(function (config) {
      var on = BF.sameConfig(config, saved);
      var row = el('li', 'bf-broncos__row' + (on ? ' is-on' : ''));
      var pick = button('', 'bf-broncos__pick', function () {
        if (onDone) onDone();
        Broncos.use(config);
      });
      pick.setAttribute('aria-pressed', on ? 'true' : 'false');
      var copy = el('span', 'bf-broncos__copy');
      copy.append(el('span', 'bf-broncos__name', fitName(config)));
      var extras = fitCardDetails(config);
      if (extras) copy.append(el('span', 'bf-broncos__extras', extras));
      pick.append(el('span', 'bf-broncos__dot'), copy);
      var rm = button('', 'bf-broncos__remove', function () {
        Broncos.remove(config);
      });
      rm.setAttribute('aria-label', 'Remove ' + fitName(config));
      rm.append(icon('close'));
      row.append(pick, rm);
      list.append(row);
    });
    var foot = el('div', 'bf-broncos__foot');
    if (menuSlot && Broncos.all().length > 1) {
      foot.append(
        button('Switch Bronco', 'bf-broncos__link bf-broncos__switch', function () {
          menuBroncos(menuSlot, true);
        })
      );
    }
    if (Broncos.all().length < Broncos.MAX) {
      foot.append(
        button('+ Add another Bronco', 'bf-broncos__link', function () {
          if (onDone) onDone();
          Garage.openAdd();
        })
      );
    }
    foot.append(
      button('Edit', 'bf-broncos__link bf-broncos__edit', function () {
        if (onDone) onDone();
        Garage.open();
      })
    );
    wrap.append(list, foot);
    return wrap;
  }

  var openDrop = null;
  function closeDrop() {
    if (!openDrop) return;
    var d = openDrop;
    openDrop = null;
    d.drop.remove();
    d.name.setAttribute('aria-expanded', 'false');
  }
  document.addEventListener('click', function (e) {
    if (openDrop && !openDrop.slot.contains(e.target)) closeDrop();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openDrop) {
      var n = openDrop.name;
      closeDrop();
      n.focus();
    }
  });

  function renderChip(slot) {
    var saved = BF.getSaved();
    var inMenu = slot.classList.contains('bf-chipslot--menu');
    if (saved && inMenu) {
      // Phone menu: the list itself, under "My Broncos".
      var box = el('div', 'bf-broncos bf-broncos--menu');
      box.append(
        el('p', 'bf-broncos__label', 'My Broncos'),
        broncoList(function () {
          menuBroncos(slot, false);
        }, slot)
      );
      slot.replaceChildren(box);
      return;
    }
    if (saved) {
      var chip;
      var toggle = function () {
        var name = chip.querySelector('.bf-chip__name');
        if (openDrop && openDrop.slot === slot) {
          closeDrop();
          return;
        }
        closeDrop();
        var drop = el('div', 'bf-broncos bf-broncos--drop');
        drop.append(broncoList(closeDrop));
        slot.append(drop);
        name.setAttribute('aria-expanded', 'true');
        openDrop = { slot: slot, drop: drop, name: name };
        var first = drop.querySelector('.bf-broncos__pick');
        if (first) first.focus();
      };
      chip = chipBox(saved, { onName: toggle });
      if (openDrop && openDrop.slot === slot) openDrop = null;
      slot.replaceChildren(chip);
      return;
    }
    var add = button('', 'bf-chip is-empty', Garage.open);
    add.append(el('span', 'bf-chip__plus', '+'), el('span', 'bf-chip__name', 'Add your Bronco'));
    slot.replaceChildren(add);
  }

  function initChips() {
    var slots = Array.prototype.slice.call(document.querySelectorAll('[data-bf-chip]'));
    if (!slots.length) return;
    var render = function () {
      slots.forEach(renderChip);
    };
    render();
    window.addEventListener(BF.CHANGE_EVENT, render);
    window.addEventListener(Broncos.LIST_EVENT, render);
    // Another tab saved or removed a Bronco.
    window.addEventListener('storage', render);
  }

  /* ------------------------ nav-row "My Bronco" --------------------- */
  // Desktop only (hidden with the nav row on phones/tablets). Left side of the nav row:
  //   SELECT YOUR BRONCO ▸
  //   MY BRONCO: RAPTOR · 4-DOOR  CHANGE

  var NAVSLOT_STEPS = ['is-compact', 'is-tiny', 'is-hidden'];

  // Step down until the content fits the space the menu leaves free.
  function fitNavSlot(slot) {
    NAVSLOT_STEPS.forEach(function (c) {
      slot.classList.remove(c);
    });
    if (!slot.offsetParent) return; // hidden (phone/tablet layout)
    for (var i = 0; i < NAVSLOT_STEPS.length; i++) {
      if (slot.scrollWidth <= slot.clientWidth + 1) return;
      slot.classList.add(NAVSLOT_STEPS[i]);
    }
  }

  function renderNavSlot(slot) {
    var saved = BF.getSaved();
    var btn = button('', 'bf-navslot__btn', Garage.open);

    if (saved) {
      btn.append(
        el('span', 'bf-navslot__label', 'My Bronco'),
        el('span', 'bf-navslot__value', shortSummary(saved)),
        el('span', 'bf-navslot__action', 'Change')
      );
      btn.setAttribute('aria-label', 'My Bronco: ' + summary(saved) + '. Change');
    } else {
      var cta = el('span', 'bf-navslot__cta');
      cta.append(
        el('span', 'bf-navslot__long', 'Select your Bronco'),
        el('span', 'bf-navslot__short', 'My Bronco'),
        ' ▸'
      );
      btn.append(cta);
      btn.setAttribute('aria-label', 'Select your Bronco');
    }

    slot.replaceChildren(btn);
    fitNavSlot(slot);
  }

  function initNavSlot() {
    var slot = document.getElementById('bf-navslot');
    if (!slot) return;
    renderNavSlot(slot);

    var refit = function () {
      fitNavSlot(slot);
    };
    if (typeof ResizeObserver === 'function' && slot.parentNode) {
      new ResizeObserver(refit).observe(slot.parentNode);
    } else {
      window.addEventListener('resize', refit);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);

    window.addEventListener(BF.CHANGE_EVENT, function () {
      renderNavSlot(slot);
    });
  }

  /* --------------------------- collection bar ------------------------- */
  // Not filtered: the Bronco picker (buildPicker). Filtered to a Bronco: the slim green card (buildBar);
  // its Change opens the picker again.

  var barKey = null;

  // JAQUAN CHANGE: the collection bar is the product page's slanted dark card (the slim
  // two-line one): "Shopping for your Raptor 4-Door" / roof and bumper, with its
  // links on the right. Green check when parts are filtered to a Bronco,
  // orange filter badge when they aren't. Picking a Bronco always uses the popup.
  var CHECK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var FILTER_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 5h16l-6.2 7.2V18l-3.6 2v-7.8z" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round"/></svg>';

  // JAQUAN CHANGE: the check animation on the collection card only plays right after
  // someone filters (popup, a "parts that fit" link, the filter sidebar, Save), never
  // on a plain page load. Filtering that loads a new page leaves a note for that page.
  var PLAY_KEY = 'bf_fit_play';
  var lastFitKey = '';

  function flagFitPlay() {
    try {
      window.sessionStorage.setItem(PLAY_KEY, '1');
    } catch (e) {}
  }

  function takeFitPlay() {
    try {
      var on = window.sessionStorage.getItem(PLAY_KEY) === '1';
      window.sessionStorage.removeItem(PLAY_KEY);
      return on;
    } catch (e) {
      return false;
    }
  }

  // Links and forms that load a page filtered to a Bronco (homepage picker, "Shop parts
  // that fit your Bronco", "Show only parts that fit"...).
  document.addEventListener(
    'click',
    function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      try {
        // Only when it adds the filter (not paging or sorting an already filtered page).
        if (BF.vehicleParamsFromSearch(window.location.search)) return;
        if (BF.vehicleParamsFromSearch(new URL(a.href, window.location.href).search)) flagFitPlay();
      } catch (err) {}
    },
    true
  );
  document.addEventListener(
    'submit',
    function (e) {
      var f = e.target;
      if (f && f.querySelector && f.querySelector('[name^="' + P + '"]')) flagFitPlay();
    },
    true
  );

  /* ------------------------ collection Bronco picker ----------------------- */
  // JAQUAN CHANGE: the collection bar is a row of dropdowns everyone knows: Trim, Doors, Roof,
  // Front bumper. The choices are the store's own filters (same list as the popup), only
  // combinations Ford made are offered. Trim and Doors are needed; Roof and Front bumper optional.
  //   - Nothing filtered: orange filter tag, "Select your Bronco", Shop parts (saves the Bronco and
  //     turns those filters on).
  //   - Filtered to a Bronco: the same bar stays, filled in, with a green check tag, "Your Bronco /
  //     Showing parts that fit" and "Show all parts". Changing a box shows "Update parts".
  //   - Phones: one compact bar (Select / Change); the boxes open in a pop-up from the bottom.
  // Each box opens our own list (white, thin lines, check on the one picked); on phones that list
  // slides up from the bottom too. Keyboard: arrows, Enter, Escape.
  var PICK_REQUIRED = 2; // trim, doors
  var PICK_SVG = {
    caret: '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M216.49,104.49l-80,80a12,12,0,0,1-17,0l-80-80a12,12,0,0,1,17-17L128,159l71.51-71.52a12,12,0,0,1,17,17Z"/></svg>',
    go: '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z"/></svg>',
    check: '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M232.49,80.49l-128,128a12,12,0,0,1-17,0l-56-56a12,12,0,1,1,17-17L96,183,215.51,63.51a12,12,0,0,1,17,17Z"/></svg>',
    close: '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z"/></svg>',
    filter: '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M230.6,49.53A15.81,15.81,0,0,0,216,40H40A16,16,0,0,0,28.19,66.76l.08.09L96,139.17V216a16,16,0,0,0,24.87,13.32l32-21.34A16,16,0,0,0,160,194.66V139.17l67.74-72.32.08-.09A15.8,15.8,0,0,0,230.6,49.53ZM146.18,130.58A8,8,0,0,0,144,136v58.66L112,216V136a8,8,0,0,0-2.16-5.47L40,56H216Z"/></svg>',
  };
  var PICK_QUESTIONS = {
    trim: 'Which trim?',
    door: 'How many doors?',
    roof: 'Hard top or soft top?',
    bumper: 'Which front bumper?',
  };

  function svgSpan(name, className) {
    var s = el('span', className);
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = PICK_SVG[name]; // static markup only
    return s;
  }

  function isPhone() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  // urlConfig: the Bronco filters in the address (null when not filtered).
  function buildPicker(url, urlConfig, saved) {
    var applied = urlConfig ? BF.sanitizeConfig(BF.toSingleConfig(urlConfig)) || {} : null;
    var fit = !!applied && Object.keys(applied).length > 0;
    var start = fit ? applied : saved || {};
    var draft = Object.assign({}, start);
    // JAQUAN CHANGE (v249): no "Shop parts" button. Once all four boxes have an answer ("Not sure"
    // counts for Roof and Front bumper), the parts filter by themselves.
    var notSure = {};

    var card = el('div', 'bf-pick' + (fit ? ' bf-pick--fit' : ''));
    card.id = BAR_ID;

    /* phones: the compact bar */
    var mini = el('div', 'bf-pick__mini');
    var miniTag = el('span', 'bf-pick__mtag');
    miniTag.innerHTML = fit ? PICK_SVG.check : PICK_SVG.filter;
    var miniText = el('div', 'bf-pick__mtext');
    var miniT = el('p', 'bf-pick__mt');
    var miniS = el('p', 'bf-pick__ms');
    miniText.append(miniT, miniS);
    // JAQUAN CHANGE (v254): phones open the same "Select your Bronco" popup as everywhere else;
    // saving it filters this collection.
    var miniBtn = button(fit ? 'Change' : 'Select', 'bf-pick__mbtn', function () {
      Garage.open();
    });
    miniBtn.setAttribute('aria-haspopup', 'dialog');
    mini.append(miniTag, miniText, miniBtn);

    /* the full bar (on phones: the pop-up) */
    var body = el('div', 'bf-pick__body');
    body.id = 'bf-pick-body';
    body.setAttribute('aria-labelledby', 'bf-pick-title');
    var sheetHead = el('div', 'bf-pick__shead');
    var sheetT = el('p', 'bf-pick__stitle', fit ? 'Your Bronco' : 'Select your Bronco');
    var sheetX = button('', 'bf-pick__sx', function () {
      closePop(true);
    });
    sheetX.setAttribute('aria-label', 'Close');
    sheetX.append(svgSpan('close', 'bf-pick__sxic'));
    sheetHead.append(sheetT, sheetX);

    var tag = el('div', 'bf-pick__tag');
    tag.innerHTML = fit ? PICK_SVG.check : PICK_SVG.filter;
    var head = el('div', 'bf-pick__head');
    var t = el('p', 'bf-pick__t', fit ? 'Your Bronco' : 'Select your Bronco');
    t.id = 'bf-pick-title';
    head.append(t, el('p', 'bf-pick__s', fit ? 'Showing parts that fit' : 'See only parts that fit'));
    var top = el('div', 'bf-pick__top');
    top.append(tag, head);

    var fieldsWrap = el('div', 'bf-pick__fields');
    var go = el('button', 'bf-pick__go');
    go.type = 'button';
    var goText = el('span', null, fit ? 'Update parts' : 'Shop parts');
    go.append(goText, svgSpan('go', 'bf-pick__goic'));
    var row = el('div', 'bf-pick__row');
    row.append(fieldsWrap, go);
    if (fit) row.append(link('Show all parts', 'bf-pick__all', withoutVehicleParams(url)));
    if (pickOpen) row.append(button('Cancel', 'bf-pick__all bf-pick__cancel', closePicker));
    body.append(sheetHead, top, row);

    var menuScrim = el('div', 'bf-pick__scrim');
    menuScrim.hidden = true;
    var popScrim = el('div', 'bf-pick__popscrim');
    popScrim.hidden = true;
    // The list backdrop sits inside the pop-up so a list opened from the pop-up shows above it.
    body.append(menuScrim);
    card.append(mini, body, popScrim);

    var facets = [];
    var openIdx = -1;
    var popped = false;

    function before(i) {
      var out = [];
      for (var j = 0; j < i; j++) {
        var v = draft[facets[j].param];
        if (v) out.push({ name: facets[j].param, value: v });
      }
      return out;
    }
    function allowed(i) {
      var f = facets[i];
      var prior = before(i);
      return f.values.filter(function (v) {
        return !BF.isOptionBlocked(f.param, v, prior);
      });
    }
    // Trim first, then Doors; Roof and Front bumper open once both are picked.
    function unlocked(i) {
      for (var j = 0; j < Math.min(i, PICK_REQUIRED); j++) {
        if (!draft[facets[j].param]) return false;
      }
      return true;
    }
    function tidy() {
      facets.forEach(function (f, i) {
        var v = draft[f.param];
        if (v && (!unlocked(i) || allowed(i).indexOf(v) === -1)) delete draft[f.param];
      });
    }
    function ready() {
      return (
        facets.length > 0 &&
        facets.slice(0, PICK_REQUIRED).every(function (f) {
          return !!draft[f.param];
        })
      );
    }
    function picked() {
      var out = {};
      facets.forEach(function (f) {
        if (draft[f.param]) out[f.param] = draft[f.param];
      });
      return out;
    }
    function changed() {
      return !fit || !BF.sameConfig(picked(), applied);
    }
    // Trim and Doors picked, and Roof and Front bumper picked or "Not sure".
    function complete() {
      return (
        ready() &&
        facets.every(function (f, i) {
          return i < PICK_REQUIRED || !!draft[f.param] || !!notSure[f.param] || allowed(i).length === 0;
        })
      );
    }
    function valueOf(facet, config) {
      for (var i = 0; i < facets.length; i++) {
        if (facets[i].facet === facet) return config[facets[i].param] || '';
      }
      return '';
    }

    function renderMini() {
      if (fit) {
        var trim = String(valueOf('trim', applied)).replace(/ Edition\b/g, '');
        miniT.textContent = [trim, valueOf('door', applied)].filter(Boolean).join(' ') || 'Your Bronco';
        miniS.textContent = [valueOf('roof', applied), valueOf('bumper', applied), 'Parts that fit'].filter(Boolean).join(' · ');
      } else {
        miniT.textContent = 'Select your Bronco';
        miniS.textContent = 'See only parts that fit';
      }
    }

    /* phone pop-up */
    function trapPop(e) {
      if (!popped) return;
      if (e.key === 'Escape' && openIdx === -1) {
        e.preventDefault();
        closePop(true);
        return;
      }
      if (e.key !== 'Tab' || openIdx !== -1) return;
      var list = Array.prototype.filter.call(body.querySelectorAll('button, a[href]'), function (n) {
        return !n.disabled && n.offsetParent !== null;
      });
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    function openPop() {
      popped = true;
      card.classList.add('is-pop');
      body.setAttribute('role', 'dialog');
      body.setAttribute('aria-modal', 'true');
      popScrim.hidden = false;
      document.documentElement.classList.add('bf-pick-lock');
      miniBtn.setAttribute('aria-expanded', 'true');
      var first = body.querySelector('.bf-pick__f:not(:disabled)');
      (first || sheetX).focus();
    }
    function closePop(focusBack) {
      if (!popped) return;
      closeMenu(false);
      popped = false;
      card.classList.remove('is-pop');
      body.removeAttribute('role');
      body.removeAttribute('aria-modal');
      popScrim.hidden = true;
      document.documentElement.classList.remove('bf-pick-lock');
      miniBtn.setAttribute('aria-expanded', 'false');
      if (pickOpen) {
        closePicker();
        return;
      }
      if (focusBack) miniBtn.focus();
    }
    popScrim.addEventListener('click', function () {
      closePop(true);
    });
    body.addEventListener('keydown', trapPop);
    // Back to a wide screen with the pop-up open: put it away.
    window.addEventListener('resize', function () {
      if (popped && !isPhone()) closePop(false);
    });

    /* the lists */
    function closeMenu(focusBox) {
      if (openIdx === -1) return;
      var i = openIdx;
      openIdx = -1;
      var cell = fieldsWrap.children[i];
      if (cell) {
        cell.classList.remove('is-open');
        var menu = cell.querySelector('.bf-pick__menu');
        if (menu) menu.hidden = true;
        var box = cell.querySelector('.bf-pick__f');
        if (box) {
          box.setAttribute('aria-expanded', 'false');
          if (focusBox) box.focus();
        }
      }
      menuScrim.hidden = true;
      if (!popped) document.documentElement.classList.remove('bf-pick-lock');
      document.removeEventListener('mousedown', outside, true);
    }
    function outside(e) {
      if (openIdx === -1) return;
      var cell = fieldsWrap.children[openIdx];
      if (cell && cell.contains(e.target)) return;
      closeMenu(false);
    }
    function openMenu(i, focusFirst) {
      closeMenu(false);
      var cell = fieldsWrap.children[i];
      if (!cell) return;
      openIdx = i;
      cell.classList.add('is-open');
      cell.querySelector('.bf-pick__f').setAttribute('aria-expanded', 'true');
      var menu = cell.querySelector('.bf-pick__menu');
      menu.hidden = false;
      if (isPhone()) {
        menuScrim.hidden = false;
        document.documentElement.classList.add('bf-pick-lock');
      }
      document.addEventListener('mousedown', outside, true);
      var opts = menu.querySelectorAll('[role="option"]');
      var on = menu.querySelector('[aria-selected="true"]') || opts[0];
      if (on) {
        if (on.scrollIntoView && menu.scrollHeight > menu.clientHeight) on.scrollIntoView({ block: 'nearest' });
        if (focusFirst) on.focus();
      }
      // Phones: focus the list itself, so no row looks picked before they tap one.
      if (!focusFirst && isPhone()) menu.focus();
    }
    function choose(i, value) {
      var f = facets[i];
      if (value) draft[f.param] = value;
      else delete draft[f.param];
      if (!value && i >= PICK_REQUIRED) notSure[f.param] = true;
      else delete notSure[f.param];
      closeMenu(false);
      render();
      if (complete() && changed()) {
        applyNow();
        return;
      }
      var next = fieldsWrap.querySelector('.is-next .bf-pick__f');
      if (next && !isPhone()) next.focus();
      else {
        var b = fieldsWrap.querySelectorAll('.bf-pick__f')[i];
        if (b) b.focus();
      }
    }
    function menuKeys(e, i) {
      var menu = e.currentTarget;
      var opts = Array.prototype.slice.call(menu.querySelectorAll('[role="option"]'));
      var at = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var n = at + (e.key === 'ArrowDown' ? 1 : -1);
        n = Math.max(0, Math.min(opts.length - 1, n));
        if (opts[n]) opts[n].focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        var o = opts[e.key === 'Home' ? 0 : opts.length - 1];
        if (o) o.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (at !== -1) choose(i, opts[at].dataset.value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeMenu(true);
      } else if (e.key === 'Tab') {
        closeMenu(false);
      }
    }
    menuScrim.addEventListener('click', function () {
      closeMenu(true);
    });

    function render() {
      tidy();
      var nextDone = false;
      fieldsWrap.replaceChildren.apply(
        fieldsWrap,
        facets.map(function (f, i) {
          var name = FACET_TITLES[f.facet] || f.facet;
          var optional = i >= PICK_REQUIRED;
          var value = draft[f.param] || '';
          var unsure = !value && optional && !!notSure[f.param];
          var on = unlocked(i) && allowed(i).length > 0;
          var isNext = on && !value && !unsure && !nextDone;
          if (isNext) nextDone = true;

          var cell = el('div', 'bf-pick__cell' + (value || unsure ? ' is-set' : '') + (on ? '' : ' is-off') + (isNext ? ' is-next' : ''));
          var menuId = 'bf-pick-menu-' + i;

          var box = el('button', 'bf-pick__f');
          box.type = 'button';
          box.disabled = !on;
          box.setAttribute('aria-haspopup', 'listbox');
          box.setAttribute('aria-expanded', 'false');
          box.setAttribute('aria-controls', menuId);
          box.setAttribute('aria-label', name + ': ' + (value || (unsure ? 'not sure' : 'not picked')));
          var txt = el('span', 'bf-pick__txt');
          txt.append(el('span', 'bf-pick__k', name));
          txt.append(el('span', 'bf-pick__v', value || (unsure ? 'Not sure' : 'Choose')));
          box.append(txt, svgSpan('caret', 'bf-pick__caret'));
          box.addEventListener('click', function () {
            if (openIdx === i) closeMenu(false);
            else openMenu(i, false);
          });
          box.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              openMenu(i, true);
            } else if (e.key === 'Escape' && openIdx === i) {
              e.preventDefault();
              e.stopPropagation();
              closeMenu(true);
            }
          });

          var menu = el('div', 'bf-pick__menu');
          menu.hidden = true;
          menu.tabIndex = -1;
          var mHead = el('div', 'bf-pick__mhead');
          var mTitle = el('p', 'bf-pick__mtitle', PICK_QUESTIONS[f.facet] || name);
          mTitle.id = menuId + '-t';
          var mClose = button('', 'bf-pick__mclose', function () {
            closeMenu(true);
          });
          mClose.setAttribute('aria-label', 'Close');
          mClose.append(svgSpan('close', 'bf-pick__mx'));
          mHead.append(mTitle, mClose);
          var list = el('ul', 'bf-pick__list');
          list.id = menuId;
          list.setAttribute('role', 'listbox');
          list.setAttribute('aria-labelledby', menuId + '-t');
          var values = allowed(i).slice();
          if (optional) values.unshift('');
          values.forEach(function (v) {
            var isOn = v ? v === value : unsure;
            var li = el('li', 'bf-pick__opt' + (isOn ? ' is-on' : ''));
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', isOn ? 'true' : 'false');
            li.tabIndex = -1;
            li.dataset.value = v;
            li.append(el('span', 'bf-pick__otext', v || 'Not sure'));
            if (isOn) li.append(svgSpan('check', 'bf-pick__ocheck'));
            li.addEventListener('click', function () {
              choose(i, v);
            });
            list.append(li);
          });
          menu.append(mHead, list);
          menu.addEventListener('keydown', function (e) {
            menuKeys(e, i);
          });
          cell.append(box, menu);
          return cell;
        })
      );
      // JAQUAN CHANGE (v249): the button is gone; picking the last answer filters the parts.
      go.hidden = true;
      go.disabled = true;
      renderMini();
    }

    function applyNow() {
      if (!ready() || !changed()) return;
      pickOpen = false;
      var config = picked();
      var prev = BF.getSaved();
      if (prev && prev[SASQ_PARAM] && prev[P + 'filter_trims'] === config[P + 'filter_trims']) {
        config[SASQ_PARAM] = prev[SASQ_PARAM]; // JAQUAN CHANGE (v254): keep the popup's Sasquatch answer
      }
      if (!BF.saveConfig(config)) return;
      var target = new URL(withConfig(window.location.href, BF.getSaved()));
      BF.markApplied(target.pathname);
      var here = new URL(window.location.href);
      here.hash = '';
      document.documentElement.classList.remove('bf-pick-lock');
      if (target.toString() === here.toString()) {
        refreshBar(true);
        return;
      }
      flagFitPlay();
      window.location.href = target.toString();
    }

    renderMini();
    if (pickOpen && isPhone()) {
      // Opened with Change on the green card: show the boxes in the pop-up once the bar is on the page.
      var tries = 0;
      (function popWhenPlaced() {
        if (card.isConnected) openPop();
        else if (tries++ < 20) setTimeout(popWhenPlaced, 50);
      })();
    }
    Garage.loadFacets().then(function (list) {
      facets = (list || []).filter(function (f) {
        return FACET_ORDER.indexOf(f.facet) !== -1 && f.values && f.values.length;
      });
      // Already filtered without a roof or bumper: those were "Not sure".
      if (fit) {
        facets.forEach(function (f, i) {
          if (i >= PICK_REQUIRED && !draft[f.param]) notSure[f.param] = true;
        });
      }
      render();
    });
    return card;
  }

  // JAQUAN CHANGE: filtered to a Bronco: the slim green card again ("Shopping for your Badlands
  // 4-Door" / roof and bumper, [Change] [Show all]). Change opens the dropdowns (filled in, green)
  // with a Cancel back to the card; on phones they open straight in the pop-up, and closing the
  // pop-up goes back to the card. Not filtered: the dropdowns.
  var pickOpen = false;

  function openPicker() {
    if (isPhone()) {
      Garage.open(); // JAQUAN CHANGE (v254): same popup on phones
      return;
    }
    pickOpen = true;
    refreshBar(true);
    if (isPhone()) return; // the picker opens its pop-up itself
    var first = document.querySelector('#' + BAR_ID + ' .bf-pick__f');
    if (first) first.focus();
  }

  function closePicker() {
    pickOpen = false;
    refreshBar(true);
  }

  function buildBar(url, urlConfig, saved) {
    if (!urlConfig || pickOpen) return buildPicker(url, urlConfig, saved);

    var shortName = function (config) {
      return fitName(config).replace(/ Edition\b/g, '').replace(/\bHeritage Limited\b/g, 'Heritage Ltd');
    };
    var state;
    var title;
    var actions = [];
    if (saved && BF.sameConfig(urlConfig, saved)) {
      state = 'fit';
      title = 'Shopping for your ' + shortName(urlConfig);
      actions.push(button('Change', 'bf-fitcard__change', openPicker));
      actions.push(link('Show all', 'bf-fitcard__change', withoutVehicleParams(url)));
    } else {
      state = 'filtered';
      title = 'Shopping for a ' + shortName(urlConfig);
      var saveable = BF.sanitizeConfig(BF.toSingleConfig(urlConfig));
      if (saveable) {
        actions.push(
          button('Save', 'bf-fitcard__change', function () {
            BF.saveConfig(saveable);
          })
        );
      }
      actions.push(link('Clear', 'bf-fitcard__change', withoutVehicleParams(url)));
    }
    var details = fitCardDetails(urlConfig);

    var card = el('div', 'bf-fitcard bf-collcard bf-collcard--' + state);
    card.id = BAR_ID;
    card.setAttribute('role', 'status');
    var badge = el('div', 'bf-fitcard__badge');
    badge.innerHTML = CHECK_SVG;
    var text = el('div', 'bf-fitcard__text');
    text.append(el('p', 'bf-fitcard__v', title));
    if (details) {
      var d = el('p', 'bf-fitcard__d');
      d.append(details);
      text.append(d);
    }
    var actionWrap = el('div', 'bf-collcard__actions');
    actions.forEach(function (x) {
      actionWrap.append(x);
    });
    card.append(badge, text, actionWrap);
    return card;
  }

  function insertBar(bar) {
    var attempts = 0;
    (function tryInsert() {
      if (bar.isConnected) return;
      var anchor =
        document.querySelector('.collection-actions-wrapper') ||
        document.querySelector('.collection-actions-filters-container');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(bar, anchor);
        var ask = document.getElementById('bf-more-ask'); // JAQUAN CHANGE (v249)
        if (ask) bar.after(ask);
        fitBarLine(bar);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () {
            fitBarLine(bar);
          });
        }
      } else if (attempts < 20) {
        attempts++;
        setTimeout(tryInsert, 150);
      }
    })();
  }

  // Keep each line of the card on one line: shrink the title (not below 15px)
  // and the line under it (not below 12px) a little at a time.
  function fitBarLine(bar) {
    if (!bar || !bar.isConnected) return;
    [['.bf-fitcard__v', 15], ['.bf-fitcard__d', 12]].forEach(function (pair) {
      var line = bar.querySelector(pair[0]);
      if (!line) return;
      line.style.fontSize = '';
      var size = parseFloat(window.getComputedStyle(line).fontSize) || 0;
      while (size > pair[1] && line.scrollWidth > line.clientWidth + 1) {
        size -= 1;
        line.style.fontSize = size + 'px';
      }
    });
  }

  var barResizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(barResizeTimer);
    barResizeTimer = setTimeout(function () {
      fitBarLine(document.getElementById(BAR_ID));
    }, 100);
  });

  function refreshBar(force, pageLoad) {
    var url = new URL(window.location.href);
    var urlConfig = BF.vehicleParamsFromSearch(url.search);
    var saved = BF.getSaved();
    if (urlConfig) BF.markApplied(url.pathname);

    // Rebuild only on change; also stops the facet observer looping on our own DOM writes.
    var key = JSON.stringify([url.search, saved, pickOpen]);
    var existing = document.getElementById(BAR_ID);
    if (!force && key === barKey && existing) return;
    barKey = key;

    var bar = buildBar(url, urlConfig, saved);
    // Animate only when the parts just got filtered to their Bronco: on page load only if
    // the page was just filtered, later only when the Bronco filter itself changed.
    var fitKey = bar.classList.contains('bf-collcard--fit') ? JSON.stringify(urlConfig) : '';
    var play = pageLoad ? takeFitPlay() : fitKey !== lastFitKey;
    // The dropdowns (Change) don't count, so Cancel doesn't replay the check animation.
    if (!bar.classList.contains('bf-pick')) lastFitKey = fitKey;
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (fitKey && play && !still) bar.classList.add('bf-fitcard--play');
    if (existing) existing.remove();
    insertBar(bar);
  }

  /* ----------------------- facet conflict guard ---------------------- */

  function applyFacetIncompatibilityRules() {
    var form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    var boxes = Array.from(form.querySelectorAll('input[type="checkbox"]'));
    var selections = boxes
      .filter(function (cb) {
        return cb.checked;
      })
      .map(function (cb) {
        return { name: cb.name || '', value: cb.value };
      });

    boxes.forEach(function (cb) {
      var wrapper = cb.closest('.checkbox-facet');
      var blocked = !cb.checked && BF.isOptionBlocked(cb.name || '', cb.value, selections);

      if (blocked && !cb.disabled) {
        cb.disabled = true;
        cb.dataset.bfBlocked = '1';
        if (wrapper) {
          wrapper.classList.add('checkbox-facet-disabled');
          wrapper.title = 'Not available with your selection';
        }
      } else if (!blocked && cb.dataset.bfBlocked) {
        cb.disabled = false;
        delete cb.dataset.bfBlocked;
        if (wrapper) {
          wrapper.classList.remove('checkbox-facet-disabled');
          wrapper.removeAttribute('title');
        }
      }
    });
  }

  var refreshScheduled = false;
  function scheduleCollectionRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    requestAnimationFrame(function () {
      refreshScheduled = false;
      applyFacetIncompatibilityRules();
      if (isCollectionPage()) refreshBar(false);
    });
  }

  function initFacetConflictGuard() {
    var form = document.getElementById('FacetFiltersForm');
    if (!form) return;
    applyFacetIncompatibilityRules();
    form.addEventListener('change', scheduleCollectionRefresh);
    var target = form.querySelector('.page-layout-sidebar-inner-content') || form;
    new MutationObserver(scheduleCollectionRefresh).observe(target, { childList: true, subtree: true });
  }

  function initCollectionPage() {
    refreshBar(true, true);
    window.addEventListener('popstate', scheduleCollectionRefresh);
    window.addEventListener(BF.CHANGE_EVENT, function () {
      refreshBar(true);
    });
  }

  /* ------------------------- homepage prefill ------------------------ */

  function waitForOptions(select, callback, timeout) {
    var start = Date.now();
    (function poll() {
      if (select.querySelectorAll('option:not(.default)').length > 0 && !select.disabled) {
        callback();
      } else if (Date.now() - start < (timeout || 5000)) {
        setTimeout(poll, 100);
      }
    })();
  }

  function initHomepageSelects() {
    var saved = BF.getSaved();
    if (!saved) return;

    document.querySelectorAll('collections-filtering').forEach(function (container) {
      var selects = Array.from(container.querySelectorAll('select.dropdown-filter')).sort(function (a, b) {
        return Number(a.dataset.index) - Number(b.dataset.index);
      });

      function applyNext(i) {
        if (i >= selects.length) return;
        var select = selects[i];
        waitForOptions(select, function () {
          var firstOption = select.querySelector('option[data-param-name]');
          var targetValue = firstOption ? saved[firstOption.dataset.paramName] : undefined;
          var match =
            targetValue !== undefined &&
            Array.from(select.options).some(function (opt) {
              return opt.value === targetValue;
            });
          if (!match) return;
          select.value = targetValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          applyNext(i + 1);
        });
      }

      applyNext(0);
    });
  }

  /* ------------------------- product fit badge ----------------------- */

  function productFitment() {
    var data = {};
    var root = document.getElementById('bf-fitment-data');
    if (!root) return data;
    root.querySelectorAll('[data-bf-key]').forEach(function (node) {
      var values = (node.dataset.bfValues || '')
        .split('|')
        .map(function (v) {
          return v.trim();
        })
        .filter(Boolean);
      if (values.length) data[node.dataset.bfKey] = values;
    });
    return data;
  }

  function reasonFor(m) {
    if (m.facet === 'sasquatch') {
      return m.mine === 'Sasquatch' ? 'Doesn\u2019t fit the Sasquatch package' : 'Needs the Sasquatch package';
    }
    if (m.compatible.length <= 2) {
      return m.compatible
        .map(function (v) {
          return BF.displayValue(m.facet, v);
        })
        .join(' or ') + ' only';
    }
    return 'Not made for ' + BF.displayValue(m.facet, m.mine);
  }

  // fitment: { key: [values] }; defaults to this product page's #bf-fitment-data.
  function fitStatus(saved, fitment) {
    var isProductPageFitment = !fitment;
    fitment = fitment || productFitment();
    var mismatches = [];
    var coreMatches = 0;
    var raptorUnknown = false;
    var root = isProductPageFitment ? document.getElementById('bf-fitment-data') : null;
    var vendor = root ? String(root.dataset.bfVendor || '') : '';
    var notes = root ? String(root.dataset.bfRaptorNotes || '').toLowerCase() : '';
    var unknownAll = (vendor === 'Barricade' || vendor === 'RedRock') &&
      (notes.indexOf('raptor fitment is not confirmed') !== -1 || notes.indexOf('raptor fitment unconfirmed') !== -1);
    var unknownLate = (vendor === 'Barricade' || vendor === 'RedRock') &&
      notes.indexOf('raptor fitment for 2025–2026 is not confirmed') !== -1;
    var selectedYear = null;
    Object.keys(saved).forEach(function (key) {
      if (BF.facetOf(key.slice(P.length)) === 'year') selectedYear = parseInt(saved[key], 10) || null;
    });

    Object.keys(saved).forEach(function (paramKey) {
      var key = paramKey.slice(P.length);
      var compatible = fitment[key];
      if (!compatible) return;
      var facet = BF.facetOf(key);
      var mine = saved[paramKey];
      var ok = compatible.some(function (v) {
        return BF.valuesEqual(facet, v, mine);
      });
      if (!ok && facet === 'trim' && BF.valuesEqual('trim', mine, 'Raptor') &&
          (unknownAll || (unknownLate && (!selectedYear || selectedYear >= 2025)))) {
        raptorUnknown = true;
        return;
      }
      if (!ok) {
        mismatches.push({ facet: facet, mine: mine, compatible: compatible });
      } else if (CORE_FACETS.indexOf(facet) !== -1) {
        coreMatches++;
      }
    });

    if (mismatches.length) {
      return { state: 'nofit', detail: mismatches.slice(0, 2).map(reasonFor).join(' · ') };
    }
    if (raptorUnknown) {
      return { state: 'unknown', detail: 'Raptor fitment is not confirmed. Contact us before ordering.' };
    }
    if (coreMatches > 0) {
      return { state: 'fit', detail: vehicleName(saved) };
    }
    return { state: 'unknown', detail: 'Contact us to confirm before ordering.' };
  }

  var BADGE = {
    ask: { icon: 'fitAsk', title: 'Will this fit your Bronco?' },
    fit: { icon: 'fitYes', title: 'Fits your ' },
    nofit: { icon: 'fitNo', title: 'Won\u2019t fit your ' },
    unknown: { icon: 'fitUnknown', title: 'Fit not confirmed' },
  };

  function renderBadge() {
    // Preferred spot: a "Bronco Fit" Custom liquid block containing
    // <div data-bf-fit-slot></div>, placed anywhere in the product editor.
    // Fallback: just above the buy area.
    var slot = document.querySelector('[data-bf-fit-slot]');
    var anchor = slot || document.querySelector('.product-actions');
    if (!anchor || !anchor.parentNode) return;

    var saved = BF.getSaved();
    var result = saved ? fitStatus(saved) : { state: 'ask', detail: '' };
    var spec = BADGE[result.state];

    if (slot && slot.dataset.bfFitStyle === 'tag') {
      renderTag(slot, saved, result);
      return;
    }

    // JAQUAN CHANGE: on the product page, a fit shows as the slanted dark card
    // ("Fits your Bronco / BADLANDS 4-DOOR / Hard top · Modular bumper").
    // Won't fit gets the same card with a red X, its reason and a link to parts that fit.
    var onProductPage = slot && slot.closest && slot.closest('.bfp');
    document.querySelectorAll('.bf-fitcard-more').forEach(function (n) { n.remove(); });
    if (onProductPage && (result.state === 'fit' || result.state === 'nofit')) {
      renderFitCard(slot, saved, result);
      markChart(saved);
      return;
    }

    var badge = el('div', 'bf-fit bf-fit--' + result.state);
    badge.setAttribute('role', 'status');

    // JAQUAN CHANGE: one line, "Fits your Raptor 4-Door", the Bronco isn't
    // repeated underneath. Won't fit keeps its reasons; no Bronco picked gets
    // a "Check fit" link instead of a button.
    var text = el('div', 'bf-fit__text');
    var titleEl = el('p', 'bf-fit__title');
    titleEl.append(
      result.state === 'fit' || result.state === 'nofit' ? fitLabel(spec.title, saved) : spec.title
    );
    text.append(titleEl);
    if (result.detail && result.state !== 'fit') text.append(el('p', 'bf-fit__detail', result.detail));

    var action = saved
      ? button('Change', 'bf-btn bf-btn--ghost bf-fit__action', Garage.open)
      : button('Check fit', 'bf-textlink bf-fit__action bf-fit__check', Garage.open);

    if (spec.icon) badge.append(icon(spec.icon, 'bf-icon bf-fit__icon'));
    // JAQUAN CHANGE: "Check fit" always opens the popup, same as "Change".
    badge.append(text, action);

    var existing = document.querySelector('.bf-fit');
    if (existing) {
      existing.replaceWith(badge);
    } else if (slot) {
      slot.append(badge);
    } else {
      anchor.parentNode.insertBefore(badge, anchor);
    }

    if (result.state === 'nofit') addFitAlternative(text, saved, slot);
    markChart(saved);
  }

  // JAQUAN CHANGE: "Hard top · Modular bumper" under the Bronco's name on the fit
  // card, from the saved Bronco's roof and bumper.
  function fitCardDetails(config) {
    var s = slotsOf(config);
    var parts = [];
    if (s.roof.length) {
      parts.push(s.roof.map(function (v) {
        return v.charAt(0) + v.slice(1).toLowerCase();
      }).join(' or '));
    }
    if (s.bumper.length) parts.push(s.bumper.join(' or ') + ' bumper');
    return parts.join(' \u00b7 ');
  }

  // JAQUAN CHANGE: the product page's "it fits" card. Dark slanted card with a green
  // slanted check badge. Plays a short one-time check animation when it appears
  // (skipped for people who turn motion off on their device).
  function renderFitCard(slot, saved, result) {
    var fits = result.state === 'fit';
    var card = el('div', 'bf-fit bf-fitcard' + (fits ? '' : ' bf-fitcard--no'));
    card.setAttribute('role', 'status');
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // The check animation only plays for a fit.
    if (fits && !still) card.classList.add('bf-fitcard--play');

    var badge = el('div', 'bf-fitcard__badge');
    badge.innerHTML = fits
      ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7 7 17" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>';

    var text = el('div', 'bf-fitcard__text');
    // "Heritage Edition 4-Door" -> "Heritage 4-Door" (and "Heritage Limited" ->
    // "Heritage Ltd") so long trims fit on one line.
    var nameEl = el('p', 'bf-fitcard__v', (fits ? 'Fits your ' : 'Won\u2019t fit your ') + fitName(saved).replace(/ Edition\b/g, '').replace(/\bHeritage Limited\b/g, 'Heritage Ltd'));
    text.append(nameEl);
    // Won't fit: the main reason only ("Raptor only"), so it stays on one line.
    var details = fits ? fitCardDetails(saved) : String(result.detail || '').split(' \u00b7 ')[0];
    var detailsEl = details ? el('p', 'bf-fitcard__d', details) : null;
    if (detailsEl) text.append(detailsEl);

    card.append(badge, text, button('Change', 'bf-fitcard__change', Garage.open));

    var existing = document.querySelector('.bf-fit');
    if (existing) {
      existing.replaceWith(card);
    } else {
      slot.append(card);
    }

    // Still too long for a narrow phone: shrink the name a little (not below 15px).
    var size = parseFloat(window.getComputedStyle(nameEl).fontSize) || 0;
    while (size > 15 && nameEl.scrollWidth > nameEl.clientWidth) {
      size -= 1;
      nameEl.style.fontSize = size + 'px';
    }
    if (detailsEl) {
      var dsize = parseFloat(window.getComputedStyle(detailsEl).fontSize) || 0;
      while (dsize > 12 && detailsEl.scrollWidth > detailsEl.clientWidth) {
        dsize -= 1;
        detailsEl.style.fontSize = dsize + 'px';
      }
    }

    // Won't fit: "See bumpers that fit" (or "Shop parts that fit your Bronco") under the card.
    if (!fits) {
      var more = el('p', 'bf-fitcard-more');
      card.after(more);
      addFitAlternative(more, saved, slot);
    }
  }

  // JAQUAN CHANGE: in the product page's fitment chart, the saved Bronco's trim row
  // is shaded (light green when that trim fits, light grey when not).
  // Cleared and redrawn whenever the saved Bronco changes.
  function markChart(saved) {
    var rows = document.querySelectorAll('.bfp__chart tbody tr');
    if (!rows.length) return;
    var trims = saved ? slotsOf(saved).trim.map(function (t) {
      return String(t).trim().toLowerCase();
    }) : [];
    Array.prototype.forEach.call(rows, function (row) {
      row.classList.remove('is-mine', 'is-mine-fits');
      var th = row.querySelector('th');
      if (!th || !trims.length) return;
      var name = th.textContent.trim().toLowerCase();
      if (trims.indexOf(name) === -1) return;
      row.classList.add('is-mine');
      if (row.querySelector('td.is-yes')) row.classList.add('is-mine-fits');
    });
  }

  // JAQUAN CHANGE: black "Your Bronco" tag for the new product page
  // (sections/bf-product.liquid, data-bf-fit-style="tag"):
  //   YOUR BRONCO / Raptor 4-Door        (check) Fits   Change
  // Won't fit and Not confirmed add a second row with the reason, and Won't
  // fit gets the "See ___ that fit" link there. No Bronco picked yet:
  //   WILL IT FIT? / Pick your Bronco to check        Check fit
  var TAG_STATUS = {
    fit: { icon: 'fitYes', text: 'Fits' },
    nofit: { icon: 'fitNo', text: 'Won\u2019t fit' },
    unknown: { icon: 'fitUnknown', text: 'Not confirmed' },
  };

  function renderTag(slot, saved, result) {
    var tag = el('div', 'bf-tag bf-tag--' + result.state);
    tag.setAttribute('role', 'status');

    var car = el('div', 'bf-tag__car');
    if (result.state === 'ask') {
      car.append(el('span', 'bf-tag__k', 'Will it fit?'), el('span', 'bf-tag__v', 'Pick your Bronco to check'));
      tag.append(car, button('Check fit', 'bf-tag__change bf-tag__check-link', Garage.open));
    } else {
      var name = fitLabel('', saved);
      name.className = 'bf-tag__v';
      car.append(el('span', 'bf-tag__k', 'Your Bronco'), name);
      var st = TAG_STATUS[result.state];
      var status = el('div', 'bf-tag__status');
      status.append(icon(st.icon, 'bf-icon'), st.text);
      var right = el('div', 'bf-tag__right');
      right.append(status, button('Change', 'bf-tag__change', Garage.open));
      tag.append(car, right);
    }

    var more = null;
    if ((result.state === 'nofit' || result.state === 'unknown') && result.detail) {
      more = el('div', 'bf-tag__more');
      more.append(el('span', 'bf-tag__reason', result.detail));
      tag.append(more);
    }

    slot.replaceChildren(tag);
    if (result.state === 'nofit' && more) addFitAlternative(more, saved, slot);
  }

  // "Won't fit" -> link to parts in the same category that do fit.
  // The category comes from the "Bronco Fit" block (data-bf-more-url/label).
  // The theme's hidden filter view tells us how many fit; if none do, the
  // link goes to all parts that fit instead.
  var altRequest = 0;

  function fitCount(collectionUrl, saved) {
    var url = new URL(withConfig(collectionUrl, saved));
    url.searchParams.set('view', 'do_not_use');
    var trimParam = P + 'filter_trims';
    var trim = saved[trimParam];
    return fetch(url.toString())
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var count = 0;
        doc.querySelectorAll('select[data-param-name="' + trimParam + '"] option').forEach(function (o) {
          if (o.value === trim) count = parseInt(o.dataset.count, 10) || 0;
        });
        return count;
      });
  }

  function addFitAlternative(text, saved, slot) {
    var req = ++altRequest;
    var moreUrl = slot && slot.dataset.bfMoreUrl;
    var moreLabel = slot && slot.dataset.bfMoreLabel;

    function show(label, href) {
      if (req !== altRequest || !text.isConnected) return;
      text.append(link(label, 'bf-textlink bf-fit__more', href));
    }
    function showAll() {
      show('Shop parts that fit your Bronco', withConfig(allProductsUrl(), saved));
    }

    if (!moreUrl || !moreLabel || !saved[P + 'filter_trims']) {
      showAll();
      return;
    }
    fitCount(moreUrl, saved)
      .then(function (n) {
        if (n > 0) {
          show('See ' + moreLabel + ' that fit (' + n + ')', withConfig(moreUrl, saved));
        } else {
          showAll();
        }
      })
      .catch(showAll);
  }

  function initProductPage() {
    renderBadge();
    window.addEventListener(BF.CHANGE_EVENT, renderBadge);
  }

  /* ------------------------- vehicle on the order -------------------- */
  // Copies the saved Bronco onto the cart as "Vehicle", so it shows on the
  // order under "Additional details". Sent once per cart + vehicle, not on
  // every page. Nothing is sent for shoppers who never pick a Bronco.

  var CART_ATTR = 'Vehicle';
  var CART_SYNC_KEY = 'bf_cart_vehicle';
  var BLOG_ATTR = 'Blog post'; // which post sent them shopping (set by the blog card)
  var BLOG_SOURCE_KEY = 'bf_blog_source';

  function cartToken() {
    var m = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    return m ? m[1] : '';
  }

  function blogSource() {
    try {
      return sessionStorage.getItem(BLOG_SOURCE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function syncCartVehicle() {
    var saved = BF.getSaved();
    var value = saved ? vehicleLine(saved) : '';
    var blog = blogSource();
    var mark = value + (blog ? '|' + blog : '');
    var last = null;
    try {
      last = sessionStorage.getItem(CART_SYNC_KEY);
    } catch (e) {}

    if (!value && !blog && !last) return; // never picked one: leave the cart alone
    if (last === cartToken() + '|' + mark) return; // already on this cart

    var attrs = {};
    attrs[CART_ATTR] = value; // '' removes it
    if (blog) attrs[BLOG_ATTR] = blog;
    fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    })
      .then(function (r) {
        if (!r.ok) return;
        try {
          if (value || blog) {
            sessionStorage.setItem(CART_SYNC_KEY, cartToken() + '|' + mark);
          } else {
            sessionStorage.removeItem(CART_SYNC_KEY);
          }
        } catch (e) {}
      })
      .catch(function () {});
  }

  function initCartVehicle() {
    syncCartVehicle();
    window.addEventListener(BF.CHANGE_EVENT, syncCartVehicle);
  }

  /* ----------------------------- blog card ---------------------------- */
  // Floating card on every blog post. It appears after a quarter of the post,
  // or sooner if the reader reaches a product or collection link first.
  // Shows the collection the post links to most:
  //   no Bronco : [photos] FRONT BUMPERS / From $849.99             (>)
  //   Bronco    : [photos] 5 FIT YOUR RAPTOR / Bumpers from $849.99 (>)
  // Posts with no collection link show best sellers from the whole store:
  //   no Bronco : SHOP BRONCO PARTS / Our best sellers
  //   Bronco    : SHOP PARTS FOR YOUR RAPTOR / Only parts that fit
  // Tapping it opens a tray with best sellers in stock (only ones that fit when
  // a Bronco is saved) and a Shop button: the top 4 for the whole store, every
  // in-stock part for a collection. The list scrolls; the header
  // and Shop button stay put. Names, photos and prices come from
  // the collection's hidden "bf_card" view (templates/collection.bf_card.json),
  // so prices are the live ones. The X hides it on this post for the visit.
  // Clicks are remembered and added to the order as "Blog post".
  //
  // Each post can override this in Shopify admin (article metafields, printed
  // by sections/main-article.liquid as #bf-tray-config):
  //   Hide tray        : no card on this post
  //   Tray products    : hand-picked parts. One: the card links straight to it.
  //                      More: the tray lists exactly these, in that order, each
  //                      marked Fits / Won't fit for the saved Bronco. Wins over
  //                      Tray collection, which then only sets the Shop button.
  //   Tray collection  : use this collection instead of the post's links
  //   Tray heading     : replaces the card and tray heading

  var BLOGCARD_CLOSED_KEY = 'bf_blogcard_closed:';

  // First link in the post to a product or collection on this store.
  function firstShopLink() {
    var root = document.querySelector('.article-template-content');
    if (!root) return null;
    return (
      Array.prototype.filter.call(root.querySelectorAll('a[href]'), function (a) {
        var u;
        try {
          u = new URL(a.getAttribute('href'), window.location.origin);
        } catch (e) {
          return false;
        }
        return u.origin === window.location.origin && /\/(products|collections)\/[^\/]+/.test(u.pathname);
      })[0] || null
    );
  }

  function collectionHandleOf(href) {
    var u;
    try {
      u = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }
    if (u.origin !== window.location.origin) return null;
    var m = u.pathname.match(/\/collections\/([^\/]+)\/?$/);
    if (!m) return null;
    try {
      return decodeURIComponent(m[1]).toLowerCase();
    } catch (e) {
      return null;
    }
  }

  function blogTopicHandles() {
    var root = document.querySelector('.article-template-content');
    if (!root) return [];
    var counts = {};
    var order = [];
    root.querySelectorAll('a[href]').forEach(function (a) {
      var handle = collectionHandleOf(a.getAttribute('href'));
      if (!handle || handle === 'all' || handle === 'frontpage') return;
      if (!counts[handle]) {
        counts[handle] = 0;
        order.push(handle);
      }
      counts[handle]++;
    });
    return order.slice().sort(function (a, b) {
      return counts[b] - counts[a] || order.indexOf(a) - order.indexOf(b);
    });
  }

  function collectionTitles() {
    var cacheKey = 'bf_collection_titles';
    try {
      var cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached) return Promise.resolve(cached);
    } catch (e) {}
    return fetch(rootUrl() + 'collections.json?limit=250')
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(function (data) {
        var map = {};
        (data.collections || []).forEach(function (c) {
          map[c.handle] = c.title;
        });
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(map));
        } catch (e) {}
        return map;
      })
      .catch(function () {
        return {};
      });
  }

  // "Front Bumpers for the 2021-2027 Ford Bronco" -> "Front Bumpers"
  // Drops everything from " for " on, plus any years or "Ford Bronco" left.
  function cleanCollectionName(text) {
    var name = String(text || '')
      .split(/\s+for\s+/i)[0]
      .replace(/\b(19|20)\d{2}\b\s*[-–]?/g, ' ')
      .replace(/\b(ford|bronco)\b/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return name || 'Bronco parts';
  }

  function shortCollectionName(handle, titles) {
    var title = titles[handle];
    if (title) return cleanCollectionName(title);
    return cleanCollectionName(
      handle
        .split('-')
        .map(function (w) {
          return w.charAt(0).toUpperCase() + w.slice(1);
        })
        .join(' ')
    );
  }

  function pickBlogTopic() {
    var handles = blogTopicHandles();
    if (!handles.length) return Promise.resolve(null);
    return collectionTitles().then(function (titles) {
      var handle = handles.filter(function (h) {
        return !(titles[h] && titles[h].indexOf('$') !== -1); // skip price collections
      })[0];
      if (!handle) return null;
      return {
        handle: handle,
        url: rootUrl() + 'collections/' + handle,
        name: shortCollectionName(handle, titles),
      };
    });
  }

  function rememberBlogClick(event) {
    try {
      sessionStorage.setItem(BLOG_SOURCE_KEY, window.location.pathname.replace(/^.*\/blogs\//, ''));
    } catch (e) {}
    if (event && event.currentTarget) queueBlogClick(event.currentTarget); // JAQUAN CHANGE
  }

  /* ------------------------ blog click tracking ---------------------- */
  // JAQUAN CHANGE: every product click on a blog post (product lines, the
  // floating card and its tray, the sidebar card) is sent to Shopify as the
  // custom event "bf_blog_product_click". The custom pixel "Blog clicks to
  // GA4" (Settings > Customer events) passes it to Google Analytics.
  // The click leaves the page straight away, so the event is saved here and
  // sent from the page the reader lands on. Nothing shows on the page.

  var BLOG_CLICK_KEY = 'bf_blog_click';

  function clickPlacement(node) {
    if (node.closest('[data-bf-inline]') || node.matches('.bf-inline')) return 'product_line';
    if (node.closest('.bf-sheet')) return 'card_tray';
    if (node.closest('.bf-blogcard')) return 'floating_card';
    if (node.closest('[data-bf-sidecard]')) return 'sidebar_card';
    return 'other';
  }

  function clickItemName(node) {
    var name =
      node.querySelector('.bf-inline__name, .bf-sheet__name, .bf-blogcard__title') ||
      node;
    return (name.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function queueBlogClick(node) {
    var href = node.getAttribute && node.getAttribute('href');
    if (!href) return;
    var dest;
    try {
      dest = new URL(href, window.location.origin);
    } catch (e) {
      return;
    }
    var data = {
      post: window.location.pathname.replace(/^.*\/blogs\//, ''),
      post_title: (document.title || '').split(' \u2013 ')[0].slice(0, 100),
      placement: clickPlacement(node),
      item: clickItemName(node),
      url: dest.pathname,
      type: /\/products\//.test(dest.pathname) ? 'product' : /\/collections\//.test(dest.pathname) ? 'collection' : 'page',
    };
    try {
      sessionStorage.setItem(BLOG_CLICK_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  function sendQueuedBlogClick() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(BLOG_CLICK_KEY);
    } catch (e) {}
    if (!raw) return;
    var tries = 0;
    (function send() {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        try {
          sessionStorage.removeItem(BLOG_CLICK_KEY);
          analytics.publish('bf_blog_product_click', JSON.parse(raw));
        } catch (e) {}
      } else if (tries++ < 20) {
        setTimeout(send, 250);
      }
    })();
  }

  function initBlogClickTracking() {
    sendQueuedBlogClick();
    // Sidebar "Your Bronco" button (bf-article.js). Only counts when it goes
    // to the parts list, not when it opens the Bronco picker.
    document.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest('[data-bf-sidecard-btn]');
      if (!btn || event.defaultPrevented) return;
      rememberBlogClick({ currentTarget: btn });
    });
  }

  function plainText(value) {
    return typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  function safePath(value) {
    return typeof value === 'string' && /^\/(?!\/)/.test(value) ? value : '';
  }

  function safeImage(value) {
    return typeof value === 'string' && /^(https:)?\/\//.test(value) ? value : '';
  }

  // Reads the collection's bf_card view: { count, from, products: [...] }.
  function loadCardData(collectionUrl, saved) {
    var url = new URL(saved ? withConfig(collectionUrl, saved) : collectionUrl, window.location.origin);
    url.searchParams.set('view', 'bf_card');
    url.searchParams.set('sort_by', 'best-selling');
    return fetch(url.toString())
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (text) {
        // Shopify wraps section output in <div class="shopify-section">.
        var data = JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
        var products = (Array.isArray(data.products) ? data.products : [])
          .map(function (p) {
            return {
              title: plainText(p && p.title),
              vendor: plainText(p && p.vendor),
              url: safePath(p && p.url),
              price: plainText(p && p.price),
              image: safeImage(p && p.image),
            };
          })
          .filter(function (p) {
            return p.title && p.url && p.price;
          });
        return {
          logo: safeImage(data.logo),
          title: plainText(data.title),
          count: Math.max(0, parseInt(data.count, 10) || 0),
          from: plainText(data.from),
          products: products,
        };
      });
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Titles are "Years Ford Bronco Type | Brand Model/Series". Years, "Ford
  // Bronco" and the brand go (the brand shows on its own line); the model or
  // series moves to the front so similar parts stay tell-apart:
  //   "2021-2027 Ford Bronco Wheel | THRET Offroad 802 Attitude" -> "802 Attitude Wheel"
  //   "2021-2027 Ford Bronco Rear Bumper | DV8 Offroad Spec Series" -> "Spec Series Rear Bumper"
  //   "2021-2027 Ford Bronco Stealth Fighter Front Bumper | Addictive Desert Designs"
  //     -> "Stealth Fighter Front Bumper"
  function shortProductName(title, vendor) {
    var cut = title.indexOf('|');
    var main = cut === -1 ? title : title.slice(0, cut);
    var tail = cut === -1 ? '' : title.slice(cut + 1).replace(/\|/g, ' ');
    if (vendor) {
      var v = escapeRegExp(vendor);
      tail = tail.replace(new RegExp('^\\s*' + v + '\\b', 'i'), '');
      main = main
        .replace(new RegExp('^\\s*' + v + '\\b', 'i'), '')
        .replace(new RegExp('\\s*[\\u2013\\u2014-]\\s*' + v + '\\s*$', 'i'), '');
    }
    main = main
      .replace(/\s+for\s+(the\s+)?(\d{2,4}\s*[-–+]\s*(\d{2,4}|present|up)?\s*)?(ford\s+)?bronco\b.*$/i, '')
      .replace(/\b(19|20)\d{2}\s*([-–]\s*((19|20)\d{2}|present|up)|\+)?/gi, '')
      .replace(/\s*&\s*bronco\s+raptor\b/gi, '')
      .replace(/\bford\s+bronco\b/gi, '')
      .replace(/^\s*bronco\b/i, '')
      .replace(/\(\s*\)/g, '');
    var name = (tail.trim() + ' ' + main)
      .replace(/\s{2,}/g, ' ')
      .replace(/^[\s,:&–—-]+|[\s,:&–—-]+$/g, '');
    return name || title;
  }

  // "Front Bumpers" -> "Bumpers"
  function lastWord(name) {
    var words = name.trim().split(/\s+/);
    return words[words.length - 1];
  }

  function thumb(src, className) {
    var box = el('span', className);
    if (src) {
      var img = el('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      box.append(img);
    }
    return box;
  }

  function buildBlogCard(view, openSheet, onClose) {
    var card = el('div', 'bf-blogcard');
    card.setAttribute('role', 'complementary');
    card.setAttribute('aria-label', 'Shop ' + view.name);

    var main;
    if (view.single) {
      // One hand-picked part: straight to its page, no tray.
      main = link('', 'bf-blogcard__main', view.single.url);
      main.addEventListener('click', rememberBlogClick);
    } else {
      main = button('', 'bf-blogcard__main', openSheet);
      main.setAttribute('aria-haspopup', 'dialog');
    }

    var thumbs = el('span', 'bf-blogcard__thumbs');
    thumbs.setAttribute('aria-hidden', 'true');
    view.products.slice(0, 3).forEach(function (p) {
      thumbs.append(thumb(p.image, 'bf-blogcard__thumb'));
    });

    var text = el('span', 'bf-blogcard__text');
    text.append(el('span', 'bf-blogcard__title', view.cardTitle), el('span', 'bf-blogcard__sub', view.cardSub));

    main.append(thumbs, text, icon('arrow', 'bf-icon bf-blogcard__go'));

    var closeBtn = button('', 'bf-blogcard__close', onClose);
    closeBtn.setAttribute('aria-label', 'Hide on this post');
    closeBtn.append(icon('close'));

    card.append(main, closeBtn);
    return card;
  }

  function buildBlogSheet(view, closeSheet) {
    var sheet = el('div', 'bf-sheet');
    sheet.hidden = true;

    var backdrop = el('div', 'bf-sheet__backdrop');
    backdrop.addEventListener('click', closeSheet);

    var panel = el('div', 'bf-sheet__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'bf-sheet-title');
    panel.tabIndex = -1;

    var grab = el('div', 'bf-sheet__grab');
    grab.setAttribute('aria-hidden', 'true');

    var list = null;

    // Fade at the bottom of the list while there's more to scroll.
    function updateFade() {
      if (list) list.classList.toggle('has-more', list.scrollTop + list.clientHeight < list.scrollHeight - 4);
    }
    sheet.bfUpdateFade = updateFade;

    // Top row: logo on the left, "See all" on the right. The title sits under
    // it; when it would only repeat the logo ("Shop Bronco parts") it's kept
    // for screen readers only. No logo, or a logo that fails to load: the
    // title takes the logo's place.
    function show(v) {
      var head = el('div', 'bf-sheet__head');
      var title = el('h2', 'bf-sheet__title', v.sheetTitle);
      title.id = 'bf-sheet-title';
      var all = link(v.count ? 'See all ' + v.count : 'See all', 'bf-sheet__all', v.shopUrl);
      all.addEventListener('click', rememberBlogClick);
      if (v.logo) {
        var logo = el('img', 'bf-sheet__logo');
        logo.alt = 'Bronco Forge';
        logo.width = 442;
        logo.height = 100;
        logo.addEventListener('error', function () {
          logo.remove();
          head.classList.remove('bf-sheet__head--logo');
          title.classList.remove('bf-visually-hidden');
          head.insertBefore(title, all);
        });
        logo.src = v.logo;
        head.classList.add('bf-sheet__head--logo');
        if (v.logoOnly) title.classList.add('bf-visually-hidden');
        head.append(logo, all);
      } else {
        head.append(title, all);
      }

      var fitRow = el('div', 'bf-sheet__fit bf-sheet__fit--' + v.fitState);
      function pick() {
        rememberBlogClick();
        closeSheet();
        // Hand-picked parts: save and stay, so the Fits / Won't fit marks update here.
        if (v.picked) Garage.openHere();
        else Garage.openFor(v.collectionUrl);
      }
      if (v.fitState === 'picked') {
        var pickedText = el('p', 'bf-sheet__fit-text');
        pickedText.append('Fit shown for your ', vehicleNameNode(v.saved));
        fitRow.append(pickedText, button('Change', 'bf-textlink bf-sheet__change', pick));
      } else if (v.fitState === 'fit') {
        var fitText = el('p', 'bf-sheet__fit-text bf-sheet__fit-mini');
        miniFit(fitText, true, v.saved);
        fitRow.append(fitText, button('Change', 'bf-textlink bf-sheet__change', pick));
      } else if (v.fitState === 'none') {
        var noneText = el('p', 'bf-sheet__fit-text');
        noneText.append('None fit your ', vehicleNameNode(v.saved), ' yet');
        fitRow.append(noneText, button('Change', 'bf-textlink bf-sheet__change', pick));
      } else {
        fitRow.append(
          el('p', 'bf-sheet__fit-text', 'Not sure what fits your Bronco?'),
          button('Pick your Bronco', 'bf-btn bf-btn--outline bf-sheet__pick', pick)
        );
      }

      list = el('ul', 'bf-sheet__list');
      list.addEventListener('scroll', updateFade, { passive: true });
      (v.general ? v.products.slice(0, 4) : v.products).forEach(function (p) {
        var item = link('', 'bf-sheet__item', p.url);
        item.addEventListener('click', rememberBlogClick);
        var info = el('span', 'bf-sheet__info');
        var priceRow = el('span', 'bf-sheet__price-row');
        priceRow.append(el('span', 'bf-sheet__price', p.price));
        // JAQUAN CHANGE: per-part labels only in hand-picked lists, where some
        // parts may not fit. In a "15 fit your Badlands" list every part fits,
        // so the row above already says it once.
        var partFit = v.picked ? p.fit : null;
        // JAQUAN CHANGE: small slanted fit label on its own line under the price.
        var chip = null;
        if (partFit === 'fit' || partFit === 'nofit') {
          chip = el('span', 'bf-sheet__chip' + (partFit === 'nofit' ? ' bf-sheet__chip--no' : ''));
          miniFit(chip, partFit === 'fit', v.saved);
        }
        info.append(el('span', 'bf-sheet__name', shortProductName(p.title, p.vendor)));
        if (p.vendor) info.append(el('span', 'bf-sheet__brand', p.vendor));
        info.append(priceRow);
        if (chip) info.append(chip);
        item.append(thumb(p.image, 'bf-sheet__thumb'), info);
        var li = el('li');
        li.append(item);
        list.append(li);
      });
      if (!v.products.length) {
        list.append(el('li', 'bf-sheet__empty', 'Nothing in stock here right now.'));
      }

      var shop = link(v.shopLabel, 'bf-btn bf-btn--primary bf-sheet__shop', v.shopUrl);
      shop.addEventListener('click', rememberBlogClick);

      var note = el('p', 'bf-sheet__note', 'Free shipping over $150 · Fitment guaranteed');

      if (title.parentNode === head) {
        panel.replaceChildren(grab, head, fitRow, list, shop, note);
      } else {
        panel.replaceChildren(grab, head, title, fitRow, list, shop, note);
      }
      requestAnimationFrame(updateFade);
    }

    show(view);
    sheet.append(backdrop, panel);
    return sheet;
  }

  // Drag the tray down to close it (from the handle or header, or from the
  // list once it's scrolled to the top).
  function enableSwipeClose(panel, closeSheet) {
    var startY = null;
    var dy = 0;
    panel.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length !== 1) return;
        var list = panel.querySelector('.bf-sheet__list');
        if (list && list.contains(e.target) && list.scrollTop > 0) return;
        startY = e.touches[0].clientY;
        dy = 0;
        panel.style.transition = 'none';
      },
      { passive: true }
    );
    panel.addEventListener(
      'touchmove',
      function (e) {
        if (startY === null) return;
        dy = Math.max(0, e.touches[0].clientY - startY);
        panel.style.transform = dy ? 'translateY(' + dy + 'px)' : '';
      },
      { passive: true }
    );
    function end() {
      if (startY === null) return;
      startY = null;
      panel.style.transition = '';
      panel.style.transform = '';
      if (dy > 80) closeSheet();
      dy = 0;
    }
    panel.addEventListener('touchend', end);
    panel.addEventListener('touchcancel', end);
  }

  function trayConfig() {
    var node = document.getElementById('bf-tray-config');
    var data = {};
    try {
      data = node ? JSON.parse(node.textContent) || {} : {};
    } catch (e) {
      data = {};
    }
    var products = (Array.isArray(data.products) ? data.products : [])
      .map(function (p) {
        var fitment = {};
        if (p && p.fitment && typeof p.fitment === 'object') {
          Object.keys(p.fitment).forEach(function (key) {
            var values = [].concat(p.fitment[key]).map(plainText).filter(Boolean);
            if (values.length) fitment[key] = values;
          });
        }
        return {
          title: plainText(p && p.title),
          vendor: plainText(p && p.vendor),
          url: safePath(p && p.url),
          price: plainText(p && p.price),
          min: plainText(p && p.min),
          cents: Math.max(0, parseInt(p && p.cents, 10) || 0),
          image: safeImage(p && p.image),
          fitment: fitment,
        };
      })
      .filter(function (p) {
        return p.title && p.url && p.price;
      });
    var c = data.collection;
    var showAt = parseInt(data.showAt, 10);
    return {
      hide: data.hide === true,
      // How far into the post (%) before the card slides up, and whether a product
      // link coming on screen shows it sooner (Blog post section, "Shopping card").
      showAt: showAt >= 5 && showAt <= 95 ? showAt / 100 : 0.5,
      early: data.early === true,
      heading: plainText(data.heading),
      collection:
        c && typeof c.handle === 'string' && /^[a-z0-9_-]+$/i.test(c.handle)
          ? { handle: c.handle, title: plainText(c.title) }
          : null,
      products: products,
    };
  }

  function collectionTopic(c) {
    return {
      handle: c.handle,
      url: rootUrl() + 'collections/' + c.handle,
      name: cleanCollectionName(c.title || c.handle),
    };
  }

  // Hand-picked parts (Tray products). Each one is checked against the saved
  // Bronco the same way as the product page badge.
  function pickedView(cfg, saved) {
    var canFit = !!(saved && saved[P + 'filter_trims']);
    var trim = canFit ? slotsOf(saved).trim.join(' or ') : '';
    var products = cfg.products.map(function (p) {
      return Object.assign({}, p, { fit: canFit ? fitStatus(saved, p.fitment).state : null });
    });
    var shop = cfg.collection ? collectionTopic(cfg.collection) : null;
    var view = {
      picked: true,
      name: cfg.heading || 'Parts in this post',
      collectionUrl: shop ? shop.url : allProductsUrl(),
      saved: canFit ? saved : null,
      fitState: canFit ? 'picked' : 'ask',
      count: 0,
      products: products,
      shopLabel: shop ? 'Shop ' + shop.name : 'Shop all Bronco parts',
      shopUrl: shop ? shop.url : allProductsUrl(),
      logo: null,
    };
    var fitWord = function (p) {
      if (p.fit === 'fit') return ' · Fits your ' + fitName(saved);
      if (p.fit === 'nofit') return ' · Won\u2019t fit your ' + fitName(saved);
      return '';
    };
    if (products.length === 1) {
      var one = products[0];
      view.single = one;
      view.cardTitle = cfg.heading || shortProductName(one.title, one.vendor);
      view.cardSub = one.price + fitWord(one);
    } else {
      var fitting = products.filter(function (p) {
        return p.fit === 'fit';
      }).length;
      var cheapest = products.slice().sort(function (a, b) {
        return a.cents - b.cents;
      })[0];
      view.cardTitle = cfg.heading || 'Shop the parts in this post';
      if (canFit) {
        view.cardSub = fitting
          ? fitting + ' of ' + products.length + ' fit your ' + trim
          : 'None fit your ' + trim;
      } else {
        view.cardSub = cheapest && cheapest.min ? 'From ' + cheapest.min : products.length + ' parts';
      }
    }
    view.sheetTitle = view.name;
    return view;
  }

  // Posts with no collection link: best sellers from the whole store.
  function generalTopic() {
    return { general: true, url: allProductsUrl(), name: 'Bronco parts' };
  }

  function cardView(topic, saved, data, fits) {
    var trim = saved ? slotsOf(saved).trim.join(' or ') : '';
    if (topic.general) {
      var general = {
        general: true,
        name: topic.name,
        collectionUrl: topic.url,
        saved: saved,
        fitState: fits ? 'fit' : saved && trim ? 'none' : 'ask',
        count: data.count,
        products: data.products,
        cardTitle: fits ? 'Shop parts for your ' + trim : 'Shop Bronco parts',
        cardSub: fits ? 'Only parts that fit' : 'Our best sellers',
        shopLabel: fits ? 'Shop parts that fit' : 'Shop all parts',
        shopUrl: fits ? withConfig(topic.url, saved) : topic.url,
        logo: data.logo,
        logoOnly: !fits, // "Shop Bronco parts" would only repeat the logo
      };
      general.sheetTitle = general.cardTitle;
      return general;
    }
    // The collection's real title (the post may link to an old address).
    var name = data.title ? cleanCollectionName(data.title) : topic.name;
    if (fits) {
      var word = lastWord(name);
      var fitTitle = data.count + (data.count === 1 ? ' fits your ' : ' fit your ') + trim;
      return {
        name: name,
        collectionUrl: topic.url,
        saved: saved,
        fitState: 'fit',
        count: data.count,
        products: data.products,
        cardTitle: fitTitle,
        cardSub: data.from ? word + ' from ' + data.from : 'Parts that fit',
        sheetTitle: fitTitle,
        shopLabel: 'Shop ' + word + ' that fit',
        shopUrl: withConfig(topic.url, saved),
        logo: data.logo,
      };
    }
    return {
      name: name,
      collectionUrl: topic.url,
      saved: saved,
      fitState: saved && trim ? 'none' : 'ask',
      count: data.count,
      products: data.products,
      cardTitle: 'Shop ' + name,
      cardSub: data.from ? 'From ' + data.from : 'Shop now',
      sheetTitle: name,
      shopLabel: 'Shop ' + name,
      shopUrl: topic.url,
      logo: data.logo,
    };
  }

  function loadCardView(topic, cfg) {
    var saved = BF.getSaved();
    if (topic.picked) return Promise.resolve(pickedView(cfg, saved));
    return loadTopicView(topic, saved).then(function (view) {
      if (view && cfg.heading) {
        view.cardTitle = view.sheetTitle = cfg.heading;
        view.logoOnly = false;
      }
      return view;
    });
  }

  function loadTopicView(topic, saved) {
    var canFit = !!(saved && saved[P + 'filter_trims']);
    var fitted = canFit ? loadCardData(topic.url, saved).catch(function () { return null; }) : Promise.resolve(null);
    return fitted.then(function (data) {
      if (data && data.count > 0 && data.products.length) return cardView(topic, saved, data, true);
      return loadCardData(topic.url, null).then(function (all) {
        return all.count > 0 && all.products.length ? cardView(topic, canFit ? saved : null, all, false) : null;
      });
    });
  }

  function initBlogCard() {
    var cfg = trayConfig();
    if (cfg.hide) return;
    var closedKey = BLOGCARD_CLOSED_KEY + window.location.pathname;
    try {
      if (sessionStorage.getItem(closedKey)) return;
    } catch (e) {}

    var topic = null;
    var card = null;
    var sheet = null;
    var reached = false;
    var lastFocus = null;
    var renderId = 0;

    function openSheet() {
      if (!sheet) return;
      lastFocus = document.activeElement;
      sheet.hidden = false;
      document.documentElement.classList.add('bf-sheet-open');
      requestAnimationFrame(function () {
        sheet.classList.add('is-open');
        if (sheet.bfUpdateFade) sheet.bfUpdateFade();
      });
      sheet.querySelector('.bf-sheet__panel').focus();
    }

    function closeSheet() {
      if (!sheet || sheet.hidden) return;
      var s = sheet;
      s.classList.remove('is-open');
      document.documentElement.classList.remove('bf-sheet-open');
      setTimeout(function () {
        s.hidden = true;
      }, 250);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function hideForPost() {
      try {
        sessionStorage.setItem(closedKey, '1');
      } catch (e) {}
      closeSheet();
      if (card) {
        var c = card;
        c.classList.remove('is-visible');
        setTimeout(function () {
          c.remove();
        }, 300);
      }
      card = null;
      document.documentElement.classList.remove('bf-blogcard-on');
      window.removeEventListener(BF.CHANGE_EVENT, render);
    }

    function reveal() {
      reached = true;
      if (!card) return;
      card.classList.add('is-visible');
      document.documentElement.classList.add('bf-blogcard-on');
    }

    // JAQUAN CHANGE: shows once the reader is cfg.showAt of the way through the post
    // (the bottom of the screen that far down the post's text; 50% unless changed in
    // the Blog post section's "Shopping card" settings). With "Show sooner" on, also
    // once the first product or collection link is in the top 70% of the screen.
    function watchScroll() {
      var anchor = cfg.early ? firstShopLink() : null;
      var body = document.querySelector('[data-bf-article-body]') || document.querySelector('.article-template-content');
      var queued = false;
      function reachedYet() {
        if (anchor && anchor.getBoundingClientRect().top <= window.innerHeight * 0.7) return true;
        if (body) {
          var r = body.getBoundingClientRect();
          if (r.height <= 0) return true;
          return (window.innerHeight - r.top) / r.height >= cfg.showAt;
        }
        var max = document.documentElement.scrollHeight - window.innerHeight;
        return max <= 0 || window.scrollY / max >= cfg.showAt;
      }
      function check() {
        queued = false;
        if (!reachedYet()) return;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        reveal();
      }
      function onScroll() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(check);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      check();
    }

    function render() {
      var id = ++renderId;
      return loadCardView(topic, cfg)
        .catch(function () {
          return null;
        })
        .then(function (view) {
          if (id !== renderId) return;
          if (sheet) sheet.remove();
          if (card) card.remove();
          sheet = card = null;
          document.documentElement.classList.remove('bf-sheet-open');
          if (!view) {
            document.documentElement.classList.remove('bf-blogcard-on');
            return;
          }
          sheet = buildBlogSheet(view, closeSheet);
          sheet.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') closeSheet();
          });
          enableSwipeClose(sheet.querySelector('.bf-sheet__panel'), closeSheet);
          card = buildBlogCard(view, openSheet, hideForPost);
          document.body.append(card, sheet);
          if (reached) reveal();
        });
    }

    var chooseTopic = cfg.products.length
      ? Promise.resolve({ picked: true })
      : cfg.collection
        ? Promise.resolve(collectionTopic(cfg.collection))
        : pickBlogTopic();
    chooseTopic.then(function (t) {
      topic = t || generalTopic();
      render();
      watchScroll();
      window.addEventListener(BF.CHANGE_EVENT, render);
    });
  }

  /* ------------------------ product lines in posts ------------------- */
  // [product 1] lines (snippets/bf-inline-product.liquid). Each carries its
  // fitment lists in data-bf-fitment; with a Bronco saved the label says
  // "Fits your Raptor 4-Door" or "Won't fit your Raptor 4-Door", otherwise it stays hidden.

  function initInlineParts() {
    var lines = document.querySelectorAll('[data-bf-inline]');
    if (!lines.length) return;

    function label() {
      var saved = BF.getSaved();
      var canFit = !!(saved && saved[P + 'filter_trims']);
      lines.forEach(function (line) {
        var chip = line.querySelector('[data-bf-inline-fit]');
        if (!chip) return;
        var state = null;
        if (canFit) {
          var fitment = {};
          try {
            var raw = JSON.parse(line.getAttribute('data-bf-fitment') || '{}') || {};
            Object.keys(raw).forEach(function (key) {
              var values = [].concat(raw[key]).map(plainText).filter(Boolean);
              if (values.length) fitment[key] = values;
            });
          } catch (e) {}
          state = fitStatus(saved, fitment).state;
        }
        chip.className = 'bf-inline__fit' + (state === 'nofit' ? ' bf-inline__fit--no' : '');
        // JAQUAN CHANGE: the label gets its own full-width line under the whole row
        // (photo, name, View), so every label in a list starts at the same spot and
        // has room for the full Bronco name on phones.
        if (chip.parentNode !== line) line.appendChild(chip);
        line.classList.add('bf-inline--mini');
        chip.classList.add('bf-inline__fit--mini');
        if (state === 'fit' || state === 'nofit') {
          var mini = el('span', '');
          miniFit(mini, state === 'fit', saved);
          chip.replaceChildren(mini);
        }
        chip.hidden = state !== 'fit' && state !== 'nofit';
      });
    }

    // JAQUAN CHANGE: product lines also show on product pages ("Completes the
    // setup", snippets/bf-pairs-with). Only blog posts record the click as the
    // post that sent them shopping.
    if (document.body.classList.contains('template-article')) {
      lines.forEach(function (line) {
        line.addEventListener('click', rememberBlogClick);
      });
    }
    label();
    window.addEventListener(BF.CHANGE_EVENT, label);
  }

  /* ---------------------------- open triggers ------------------------ */
  // Any element with [data-bf-garage], or a link to #my-bronco, opens the panel.

  function initTriggers() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-bf-garage], a[href$="#my-bronco"]');
      if (!trigger) return;
      event.preventDefault();
      Garage.open();
    });
    if (window.location.hash === '#my-bronco') {
      // JAQUAN CHANGE: open once, then drop #my-bronco from the address.
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (e) {}
      Garage.open();
    }
  }

  /* ---------- JAQUAN CHANGE (v249): 3 quick extra questions: Year, Tires, Lift ----------
     Optional, one tap each, saved right away. Asked in two places:
     A) the Bronco popup, right after their Bronco saves ("Want better matches?");
     B) one question under the Bronco bar on pages where it matters (tires on wheels/bumpers,
        lift on steps/sliders/suspension). Answers stay on this device (bf_bronco_more) and go on
        their Klaviyo profile (Bronco Year, Bronco Tires, Bronco Lift) when we know their email. */
  var MORE_KEY = 'bf_bronco_more';
  var MORE_LATER = 'bf_more_later'; // "Maybe later": not again this visit
  var MORE_SKIP = 'bf_more_skip_'; // "Not now" on a page question: not again on this device
  var MORE_PROPS = { Year: 'Bronco Year', Tires: 'Bronco Tires', Lift: 'Bronco Lift' };

  function moreGet() {
    try {
      var m = JSON.parse(localStorage.getItem(MORE_KEY) || '{}');
      return m && typeof m === 'object' ? m : {};
    } catch (e) {
      return {};
    }
  }

  // "Yes, stock 37\u201d" -> "Stock 37\u201d", "No, stock height" -> "Stock height".
  function moreClean(label) {
    var v = String(label).replace(/^(Yes|No), /, '');
    return v.charAt(0).toUpperCase() + v.slice(1);
  }

  function moreSet(key, value) {
    var m = moreGet();
    m[key] = value;
    m._for = moreFor(BF.getSaved());
    try {
      localStorage.setItem(MORE_KEY, JSON.stringify(m));
    } catch (e) {}
    var props = {};
    props[MORE_PROPS[key]] = value;
    sendProfile(props);
  }

  // JAQUAN CHANGE (v253): puts properties on the shopper's Klaviyo profile, only when we know who they
  // are: the email they typed into "Email it to me" in the cart, or a shopper Klaviyo already recognizes
  // (clicked one of our emails, signed up on a form). Anonymous shoppers: nothing is sent.
  // Resolves true when it was sent.
  function sendProfile(props) {
    var email = null;
    try {
      email = JSON.parse(localStorage.getItem('bf_save_email') || 'null');
    } catch (e) {}
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return klaviyoPost('profiles', { data: { type: 'profile', attributes: { email: email, properties: props } } }).then(
        function () {
          return true;
        },
        function () {
          return false;
        }
      );
    }
    var k = window.klaviyo;
    if (!k || typeof k.identify !== 'function') return Promise.resolve(false);
    if (typeof k.isIdentified !== 'function') {
      // Can't tell who they are: hand it to Klaviyo anyway (it keeps it if it knows them), try again later.
      try {
        k.identify(props);
      } catch (e) {}
      return Promise.resolve(false);
    }
    var known = Promise.resolve(k.isIdentified());
    return known.then(
      function (yes) {
        if (!yes) return false;
        k.identify(props);
        return true;
      },
      function () {
        return false;
      }
    );
  }

  // JAQUAN CHANGE (v253): the saved Bronco goes on the Klaviyo profile as Bronco Trim, Bronco Doors,
  // Bronco Roof, Bronco Bumper and Bronco Setup (same names the old popup sign-up box used). Sent once per
  // Bronco; if the shopper isn't known yet, it tries again on later pages until they are.
  var PROFILE_SENT = 'bf_profile_bronco';
  function broncoProfileSync() {
    var saved = BF.getSaved();
    if (!saved) return;
    var s = slotsOf(saved);
    var props = {
      'Bronco Trim': s.trim.join(' or '),
      'Bronco Doors': s.door.join(' or '),
      'Bronco Roof': s.roof.join(' or ') || 'Not sure',
      'Bronco Bumper': s.bumper.join(' or ') || 'Not sure',
      'Bronco Setup': vehicleName(saved)
    };
    if (String(saved[P + 'filter_trims'] || '').toLowerCase() !== 'raptor') {
      props['Bronco Sasquatch'] = saved[SASQ_PARAM] === 'Sasquatch' ? 'Yes' : saved[SASQ_PARAM] === 'No Sasquatch' ? 'No' : 'Not sure';
    }
    var key = JSON.stringify(props);
    try {
      if (localStorage.getItem(PROFILE_SENT) === key) return;
    } catch (e) {}
    sendProfile(props).then(function (sent) {
      if (!sent) return;
      try {
        localStorage.setItem(PROFILE_SENT, key);
      } catch (e) {}
    });
  }

  // JAQUAN CHANGE (v252): the Year / Tires / Lift answers belong to one Bronco. Removing the Bronco,
  // or saving a different trim or doors, clears them (and "Maybe later"), so the questions show again.
  function moreFor(config) {
    if (!config) return '';
    return (config[P + 'filter_trims'] || '') + '|' + (config[P + 'filter_door_config'] || '');
  }

  function moreSync() {
    var saved = BF.getSaved();
    var m = moreGet();
    var now = moreFor(saved);
    if (!saved || (m._for && m._for !== now)) {
      try {
        localStorage.removeItem(MORE_KEY);
        sessionStorage.removeItem(MORE_LATER);
      } catch (e) {}
      return;
    }
    // Answers saved before this change: tie them to the Bronco saved now.
    if (!m._for && Object.keys(m).length) {
      m._for = now;
      try {
        localStorage.setItem(MORE_KEY, JSON.stringify(m));
      } catch (e) {}
    }
  }

  function moreLater() {
    try {
      return sessionStorage.getItem(MORE_LATER) === '1';
    } catch (e) {
      return false;
    }
  }

  function trimSpec(config) {
    var t = String((config && config[P + 'filter_trims']) || '').toLowerCase();
    var key = Object.keys(BF.TRIMS || {}).filter(function (k) {
      return k.toLowerCase() === t;
    })[0];
    return key ? BF.TRIMS[key] : null;
  }

  // The questions for this Bronco, in order. Years: only the ones Ford made this trim (and doors).
  function moreQuestions(config) {
    var spec = trimSpec(config);
    var door = config && config[P + 'filter_door_config'];
    var years = spec
      ? Object.keys(spec).filter(function (y) {
          return !door || spec[y].indexOf(door) !== -1;
        })
      : ['2021', '2022', '2023', '2024', '2025', '2026'];
    years.sort();
    var raptor = String((config && config[P + 'filter_trims']) || '').toLowerCase() === 'raptor';
    return [
      { key: 'Year', q: 'What year is it?', why: 'Some parts only fit certain years. This keeps fit checks exact.', cols: 3, opts: years },
      raptor
        ? { key: 'Tires', q: 'Still on the stock 37” tires?', why: 'So we can point out wheels and bumpers that clear.', cols: 2, opts: ['Yes, stock 37”', '35”', '38”+', 'Other'], yes: 0 }
        : { key: 'Tires', q: 'What size tires are on it?', why: 'So we can point out wheels and bumpers that clear.', cols: 2, opts: ['Stock', '33”', '35”', '37”+'] },
      { key: 'Lift', q: 'Is it lifted?', why: 'Steps, sliders and suspension parts depend on it.', cols: 2, opts: ['No, stock height', 'Leveled', '2–3” lift', '4”+ lift'], yes: 0 }
    ];
  }

  function moreLeft(config) {
    var have = moreGet();
    return moreQuestions(config).filter(function (q) {
      return !have[q.key];
    });
  }

  // One question: words, why it helps, a row of tap buttons.
  function moreQuestionEl(q, onPick, extraClass) {
    var wrap = el('div', 'bf-mq' + (extraClass ? ' ' + extraClass : ''));
    var t = el('div', 'bf-mq__t');
    t.append(el('p', 'bf-mq__q', q.q), el('p', 'bf-mq__why', q.why));
    var chips = el('div', 'bf-mq__chips bf-mq__chips--' + q.cols);
    q.opts.forEach(function (o, i) {
      var b = button(o, 'bf-mq__chip' + (q.yes === i ? ' is-yes' : ''), function () {
        moreSet(q.key, moreClean(o));
        onPick(o);
      });
      chips.append(b);
    });
    wrap.append(t, chips);
    return wrap;
  }

  // B) the page question under the Bronco bar.
  var ASK_ID = 'bf-more-ask';
  function moreAskKey() {
    var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    var h = m ? m[1] : '';
    if (/wheel|tire|bumper|fender|bull-bar/.test(h)) return 'Tires';
    if (/side-step|slider|suspension|lift/.test(h)) return 'Lift';
    return '';
  }

  function placeAsk(ask) {
    var bar = document.getElementById(BAR_ID);
    if (bar && bar.isConnected) {
      bar.after(ask);
      return true;
    }
    var anchor = document.querySelector('.collection-actions-wrapper') || document.querySelector('.collection-actions-filters-container');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(ask, anchor);
      return true;
    }
    return false;
  }

  function initMoreAsk() {
    var old = document.getElementById(ASK_ID);
    var key = moreAskKey();
    var saved = BF.getSaved();
    var skipped = false;
    try {
      skipped = localStorage.getItem(MORE_SKIP + key) === '1';
    } catch (e) {}
    if (!key || !saved || moreGet()[key] || skipped) {
      if (old && !old.classList.contains('is-done')) old.remove();
      return;
    }
    if (old) return;
    var q = moreQuestions(saved).filter(function (x) {
      return x.key === key;
    })[0];
    var ask = el('div', 'bf-ask2');
    ask.id = ASK_ID;
    var thanks = el('p', 'bf-ask2__done');
    thanks.hidden = true;
    var row = moreQuestionEl(q, function (o) {
      row.hidden = true;
      later.hidden = true;
      thanks.hidden = false;
      thanks.textContent = 'Got it: ' + moreClean(o) + '. Thanks!';
      ask.classList.add('is-done');
    });
    var later = button('Not now', 'bf-ask2__later', function () {
      try {
        localStorage.setItem(MORE_SKIP + key, '1');
      } catch (e) {}
      ask.remove();
    });
    ask.append(row, later, thanks);
    var tries = 0;
    (function put() {
      if (placeAsk(ask) || tries++ > 20) return;
      setTimeout(put, 150);
    })();
  }

  /* ------------------------------- boot ------------------------------ */

  ready(function () {
    moreSync(); // JAQUAN CHANGE (v252): first, so everything below sees the right answers
    window.addEventListener(BF.CHANGE_EVENT, moreSync);
    // JAQUAN CHANGE (v253): the Bronco on the Klaviyo profile. After Klaviyo's script has loaded.
    setTimeout(broncoProfileSync, 2500);
    window.addEventListener(BF.CHANGE_EVENT, broncoProfileSync);
    // Nav-row "My Bronco" item is turned off. To bring it back, uncomment the
    // next line and delete the ".bf-navslot { display: none !important; }" rule.
    // initNavSlot();
    initChips(); // JAQUAN CHANGE: header / phone menu "My Bronco" box
    // JAQUAN CHANGE: fetch the popup's choices once the page is idle, so the first open is instant.
    (window.requestIdleCallback || function (fn) { return setTimeout(fn, 1500); })(function () {
      Garage.loadFacets();
    });
    initTriggers();
    initCartVehicle();
    initBlogClickTracking(); // JAQUAN CHANGE
    if (isCollectionPage()) {
      initCollectionPage();
      // JAQUAN CHANGE (v254): the Tires / Lift question under the bar is gone.
    }
    if (document.getElementById('FacetFiltersForm')) initFacetConflictGuard();
    if (document.querySelector('collections-filtering')) initHomepageSelects();
    if (isProductPage()) {
      initProductPage();
      initInlineParts(); // JAQUAN CHANGE: "Completes the setup" fit labels
    }
    if (document.body.classList.contains('template-article')) {
      initBlogCard();
      initInlineParts();
    }
  });
})();

